import React, { useState, useEffect, useRef, useMemo } from "react";
import { shoplineFields, isShoplineSuggestion } from "../../../utils/shoplineSuggestion";
import { isEmbedded } from "../../../utils/shoplineEmbedded";
import { getContentLanguage } from "../../../utils/contentLanguage";

import {
  Box, Typography, Stack, Button, IconButton, Grid, Skeleton,
  Dialog, DialogContent, TextField, FormControl, Select, MenuItem, Tabs, Tab,
  CircularProgress, InputAdornment,
} from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import StarIcon from "@mui/icons-material/Star";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

import { getCookie } from "../../../utils/storageUtils";
import {
  useAuthorityEngineSuggestionsTriggerMutation, useLazyGetTaskStatusQuery,
  useGetBlogSuggestionsQuery, useGetCourseSuggestionsQuery,
  useGeneratePostMutation, useGetInitiatedPostsQuery, useActivatePostMutation,
  useUpdatePostMutation, useSchedulePostMutation, useGetPlatformStatusCountQuery,
} from "../../../redux/api/promptApi";
import { useGetUserDetailsMutation } from "../../../redux/api/authApi";
import { useTTSQueue } from "hooks/useTTSQueue";
import CustomizeBlogDialog from "../../../components/CustomizeBlogDialog";
import CustomizeCourseDialog from "../../../components/CustomizeCourseDialog";
import ContentLanguageSelect from "../../../components/ContentLanguageSelect";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TuneIcon from '@mui/icons-material/Tune';
import LanguageIcon from "@mui/icons-material/Language";
import MyDigitalTwinPopup from "../../../components/MyDigitalTwinPopup";
import UserImg from "../../../assets/podcast_monster/user_img.png";
import OceshaLogo from "../../../assets/ocesha_images/ocesha_logo.png";
import OceshaLogoIco from "../../../assets/ocesha_images/ocesha_logo_icon.png";
import { useAppSelector } from "../../../redux/store";
import {
  usePlanDetailsMutation,
  useLazyGetProductQuery,
} from "../../../redux/api/productApi";
// Cache version — bump whenever the cached data shape changes so old caches
import { useCourseGeneration } from "../../../contexts/CourseGenerationContext";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

// from previous deployments get auto-discarded on load.
// SHOPLINE merchants get product social posts + product blogs only (no course / video ideas).
const productOnlyMode = () => isEmbedded();

const CACHE_VERSION = 4; // v4: SHOPLINE merchants: product posts + blogs only
// Cache lifetime — 1 day. After this the cache is discarded and data is re-fetched.
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
// Single key for the whole suggestions cache.
const CACHE_KEY = "authority_engine_cache";

// ── Suggestions per language ────────────────────────────────────────
// Switching language no longer regenerates the suggestion cards with the AI. The set in
// the old language is kept as "authority_engine_cache__<lang>"; the new language reuses its
// own set when it has one, otherwise the existing cards are translated by the backend,
// which stores every translation in the database (each text is translated only once).
const LANG_CACHE_KEY = (lang: string) => `${CACHE_KEY}__${lang}`;
const SUGGESTION_BUCKETS = ["authorityData", "blogTriggerSuggestions", "newCourseSuggestions",
  "randomCourseSuggestion", "videoSuggestions"];

const isUsableCache = (c: any) =>
  !!c && c.__version === CACHE_VERSION && !!c.__cachedAt && Date.now() - c.__cachedAt <= CACHE_TTL_MS;

const readLangCache = (lang: string): any => {
  try {
    const c = JSON.parse(localStorage.getItem(LANG_CACHE_KEY(lang)) || "null");
    return isUsableCache(c) ? c : null;
  } catch { return null; }
};

const stashCurrentCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const c = JSON.parse(raw);
    if (isUsableCache(c)) localStorage.setItem(LANG_CACHE_KEY(c.__lang || "en"), raw);
  } catch { /* ignore */ }
};

/** The most recent usable suggestion set in any other language (source for translation) */
const findSourceCache = (exceptLang: string): any => {
  let best: any = null;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i) || "";
      if (!k.startsWith(`${CACHE_KEY}__`) || k === LANG_CACHE_KEY(exceptLang)) continue;
      const c = JSON.parse(localStorage.getItem(k) || "null");
      if (isUsableCache(c) && (!best || c.__cachedAt > best.__cachedAt)) best = c;
    }
  } catch { /* ignore */ }
  return best;
};

const clearAllLangCaches = () => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i) || "";
      if (k.startsWith(`${CACHE_KEY}__`)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch { /* ignore */ }
};

/** Source suggestion set -> the same cards in `lang` (backend: stored translations) */
const translateSuggestionCache = async (source: any, lang: string): Promise<any> => {
  const data: Record<string, any> = {};
  SUGGESTION_BUCKETS.forEach((b) => { if (source?.[b] != null) data[b] = source[b]; });
  const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
  const res = await axios.post(`${apiUrl}/api/i18n/translate-content`, { lang, data }, { timeout: 120000 });
  const translated = res?.data?.data;
  if (!translated || typeof translated !== "object") throw new Error("no translation returned");
  return { ...source, ...translated, __lang: lang, __cachedAt: source.__cachedAt };
};

const ALL_PLATFORMS_WITH_AUTHORITY = "linkedin | twitter | facebook | youtube | authority";

// Builds the "/course/:slug" view URL — same slug logic as listing-courses.tsx
// (lowercase title + id) so links generated here resolve to the same route.
const createCourseSlug = (title: string, id: string) => {
  const slug = title
    ?.toLowerCase()
    ?.trim()
    ?.replace(/[^a-z0-9\s-]/g, "")
    ?.replace(/\s+/g, "-");

  return `${slug}-${id}`;
};

// Default quantities used the very first time a user hits the page, before
// they've ever saved anything via the "Tell me what to create" popup —
// mirrors that popup's own defaults (2 of each type).
const DEFAULT_CONTENT_TYPES: { type: string; quantity: number }[] = [
  { type: "social_posts", quantity: 2 },
  { type: "blogs", quantity: 2 },
  { type: "videos", quantity: 2 },
  { type: "courses", quantity: 3 },
  { type: "images", quantity: 2 },
];

// Reads the last content_types/mode the "Tell me what to create" popup saved
// to localStorage (same 24h-expiry pattern as the digital twin prompt). Falls
// back to DEFAULT_CONTENT_TYPES + "recommend" if nothing valid is stored yet,
// so both the initial page load AND a plain header-button refresh always send
// a content_types payload instead of omitting it.

const readStoredContentTypesAndMode = (): {
  content_types: { type: string; quantity: number }[];
  mode: "recommend" | "recommend_generate";
  knowledge_mode: "faithful" | "balanced" | "expanded";
} => {
  const validKnowledgeModes = ["faithful", "balanced", "expanded"];

  try {
    const raw = localStorage.getItem("digital_twin_content_types");
    if (raw) {
      const parsed = JSON.parse(raw);
      const isExpired = Date.now() - parsed.timestamp > 24 * 60 * 60 * 1000;
      if (!isExpired) {
        const knowledge_mode = validKnowledgeModes.includes(parsed.knowledge_mode)
          ? (parsed.knowledge_mode as "faithful" | "balanced" | "expanded")
          : "balanced";

        return {
          content_types: Object.entries(parsed.counts || {}).map(
            ([type, quantity]) => ({ type, quantity: quantity as number })
          ),
          mode: parsed.mode || "recommend",
          knowledge_mode,
        };
      }
    }
  } catch {
    // ignore malformed/missing cache — fall through to defaults
  }
  return { content_types: DEFAULT_CONTENT_TYPES, mode: "recommend", knowledge_mode: "balanced" };
};

// const readStoredContentTypesAndMode = (): {
//   content_types: { type: string; quantity: number }[];
//   mode: "recommend" | "recommend_generate";
//   knowledge_mode: "faithful" | "balanced" | "expanded";
// } => {

//   const validKnowledgeModes = ["faithful", "balanced", "expanded"];
//   const storedKnowledgeMode = localStorage.getItem("digital_twin_knowledge_mode");
//   const knowledge_mode = validKnowledgeModes.includes(storedKnowledgeMode || "")
//     ? (storedKnowledgeMode as "faithful" | "balanced" | "expanded")
//     : "balanced";

//   try {
//     const raw = localStorage.getItem("digital_twin_content_types");
//     if (raw) {
//       const parsed = JSON.parse(raw);
//       const isExpired = Date.now() - parsed.timestamp > 24 * 60 * 60 * 1000;
//       if (!isExpired) {
//         return {
//           content_types: Object.entries(parsed.counts || {}).map(
//             ([type, quantity]) => ({ type, quantity: quantity as number })
//           ),
//           mode: parsed.mode || "recommend",
//           knowledge_mode,
//         };
//       }
//     }
//   } catch {
//     // ignore malformed/missing cache — fall through to defaults
//   }
//   return { content_types: DEFAULT_CONTENT_TYPES, mode: "recommend", knowledge_mode }

 
// };

// ─── Platform chip ───────────────────────────────────────────────
const PlatformChip = ({
  platform, type = "default",
}: { platform: string; type?: "default" | "icon"; }) => {
  const cfg: Record<string, { icon: React.ReactNode; label: string; color: string; bg: string; }> = {
    linkedin: { icon: <LinkedInIcon sx={{ fontSize: 13 }} />, label: "LinkedIn", color: "#5EB0E5", bg: "rgba(10,102,194,0.22)" },
    facebook: { icon: <FacebookIcon sx={{ fontSize: 13 }} />, label: "Facebook", color: "#6AB0F5", bg: "rgba(24,119,242,0.22)" },
    twitter: { icon: <TwitterIcon sx={{ fontSize: 13 }} />, label: "Twitter", color: "#5EC5F5", bg: "rgba(29,161,242,0.22)" },
    youtube: { icon: <YouTubeIcon sx={{ fontSize: 13 }} />, label: "YouTube", color: "#FF0000", bg: "rgba(255,0,0,0.22)" },
    authority: {
      icon: (
        <Box
          component="img"
          src={OceshaLogoIco}
          alt="Ocesha"
          sx={{
            //width: 20,
            //height: 20,
            objectFit: "contain",
            borderRadius: "50%",
          }}
        />
      ),
      color: "#FFFFFF",
      bg: "#000000",
      label: "Authority",
    },
  };
  const p = cfg[platform];
  if (!p) return null;
  if (type === "icon") {
    return (
      <Box sx={{ width: 32, height: 32, borderRadius: "8px", background: p.bg, color: p.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {p.icon}
      </Box>
    );
  }
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.45, borderRadius: "7px", background: p.bg, color: p.color, fontSize: "12px", fontWeight: 500 }}>
      {p.icon} {p.label}
    </Box>
  );
};

const TypeChip = ({
  platform, type = "default", postType,
}: { platform: string; type?: "default" | "icon" | "label"; postType?: string; }) => {
  const cfg: Record<string, { icon: React.ReactNode; label: string; color: string; bg: string; }> = {
    blog: { icon: <ArticleOutlinedIcon sx={{ fontSize: 13 }} />, label: "Blog", color: "#A78BFA", bg: "rgba(139,92,246,0.22)" },
    course: { icon: <SchoolOutlinedIcon sx={{ fontSize: 13 }} />, label: "Course", color: "#34D399", bg: "rgba(16,185,129,0.18)" },
    post: postType === "video"
      ? {
        icon: <PlayCircleOutlinedIcon sx={{ fontSize: 13 }} />,
        label: "Clip",
        color: "#FFFFFF",
        bg: "#F97316",
      }
      : {
        icon: <ChatBubbleOutlineIcon sx={{ fontSize: 13 }} />,
        label: "Post",
        color: "#FFFFFF",
        bg: "#08A070",
      },
  };
  const p = cfg[platform];
  if (!p) return null;
  if (type === "icon") {
    return (
      <Tooltip title={p.label} arrow key={platform}>
        <Box sx={{ width: 32, height: 32, borderRadius: "8px", background: p.bg, color: p.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {p.icon}
        </Box>
      </Tooltip>
    );
  }

  if (type === "label") {
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.45, borderRadius: "7px", background: p.bg, color: p.color, fontSize: "12px", fontWeight: 500 }}>
        {p.label}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.45, borderRadius: "7px", background: p.bg, color: p.color, fontSize: "12px", fontWeight: 500 }}>
      {p.icon} {p.label}
    </Box>
  );
};

const DarkCard = ({ children, sx }: { children: React.ReactNode; sx?: object; }) => (
  <Box sx={{
    borderRadius: "14px",
    background: "linear-gradient(180deg, rgba(17,24,39,0.96) 0%, rgba(8,12,20,0.96) 100%)",
    border: "1px solid rgba(255,255,255,0.06)",
    overflow: "hidden", p: 2.5, ...sx,
  }}>
    {children}
  </Box>
);

const GradientBtn = ({ label, onClick, loading = false, gradient }: {
  label: string; onClick?: () => void; loading?: boolean; gradient?: string;
}) => (
  <Button
    fullWidth variant="contained" onClick={onClick} disabled={loading}
    startIcon={!loading ? <AutoAwesomeIcon sx={{ fontSize: 13 }} /> : null}
    sx={{
      background: gradient || "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)",
      textTransform: "none", color: "#fff", borderRadius: "12px",
      fontSize: "13px", fontWeight: 600, boxShadow: "none", py: 1.25,
      "&:hover": { background: gradient, opacity: 0.92, boxShadow: "none" },
      "&.Mui-disabled": { background: gradient, opacity: 0.6, color: "#fff" },
    }}
  >
    {loading ? "Generating..." : label}
  </Button>
);

const PostSkeleton = () => (
  <Box sx={{ p: 2, background: "#15151D", borderRadius: "14px" }}>
    <Stack direction="row" spacing={2}>
      <Skeleton variant="rectangular" width={180} height={140} sx={{ borderRadius: "8px" }} />
      <Stack spacing={1} flex={1}>
        <Skeleton width="40%" height={14} />
        <Skeleton width="80%" height={18} />
        <Skeleton width="100%" height={14} />
        <Skeleton width="60%" height={14} />
        <Skeleton width="30%" height={30} />
      </Stack>
    </Stack>
  </Box>
);

const RecommendationCardSkeleton = ({ label }: { label?: string }) => (
  <Box sx={{
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px", height: "100%",
    display: "flex", flexDirection: "column", overflow: "hidden",
  }}>
    <Skeleton variant="rectangular" width="100%" height={130} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", flex: 1 }}>
      <Stack spacing={1.5} height="100%">
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Skeleton variant="circular" width={38} height={38} sx={{ bgcolor: "rgba(255,255,255,0.05)", flexShrink: 0 }} />
          <Stack spacing={0.6} flex={1}>
            <Skeleton variant="text" width="40%" height={12} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
            <Skeleton variant="text" width="55%" height={14} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
          </Stack>
        </Stack>
        <Skeleton variant="text" width="90%" height={22} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
        <Skeleton variant="text" width="70%" height={22} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
        <Skeleton variant="text" width="100%" height={14} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
        <Skeleton variant="text" width="95%" height={14} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
        <Skeleton variant="text" width="60%" height={14} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
        <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: "12px", bgcolor: "rgba(255,255,255,0.05)" }} />
        <Skeleton variant="rectangular" width="100%" height={42} sx={{ borderRadius: "12px", bgcolor: "rgba(255,255,255,0.05)" }} />

        {label && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ mt: "auto", py: 1 }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 15, color: "rgba(255,255,255,0.5)" }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "rgba(255,255,255,0.55)", textAlign: "center" }}>
              {label}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  </Box>
);

// Course cards take noticeably longer to generate than blog/social ones, so
// this skeleton carries its own status copy + subtle pulse rather than
// sitting there as a blank placeholder — it tells the user something is
// actively happening, not that the page is stuck.
const CourseRecommendationSkeleton = ({ label = "Building your course recommendations..." }: { label?: string }) => (
  <Box sx={{
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(139,92,246,0.18)",
    borderRadius: "16px", height: "100%",
    display: "flex", flexDirection: "column", overflow: "hidden",
    animation: "coursePulse 1.8s ease-in-out infinite",
    "@keyframes coursePulse": {
      "0%, 100%": { opacity: 1 },
      "50%": { opacity: 0.6 },
    },
  }}>
    <Skeleton variant="rectangular" width="100%" height={130} sx={{ bgcolor: "rgba(139,92,246,0.08)" }} />
    <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", flex: 1 }}>
      <Stack spacing={1.5} height="100%">
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Skeleton variant="circular" width={38} height={38} sx={{ bgcolor: "rgba(139,92,246,0.1)", flexShrink: 0 }} />
          <Stack spacing={0.6} flex={1}>
            <Skeleton variant="text" width="40%" height={12} sx={{ bgcolor: "rgba(139,92,246,0.1)" }} />
            <Skeleton variant="text" width="55%" height={14} sx={{ bgcolor: "rgba(139,92,246,0.1)" }} />
          </Stack>
        </Stack>
        <Skeleton variant="text" width="90%" height={22} sx={{ bgcolor: "rgba(139,92,246,0.1)" }} />
        <Skeleton variant="text" width="70%" height={22} sx={{ bgcolor: "rgba(139,92,246,0.1)" }} />
        <Skeleton variant="text" width="100%" height={14} sx={{ bgcolor: "rgba(139,92,246,0.1)" }} />
        <Skeleton variant="text" width="95%" height={14} sx={{ bgcolor: "rgba(139,92,246,0.1)" }} />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ mt: "auto", py: 1 }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 15, color: "#8B5CF6" }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#B695F8", textAlign: "center" }}>
            {label}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  </Box>
);

const tabs = [
  { key: "all", label: "All Content" },
  { key: "ready", label: "Ready to Publish" },
  { key: "scheduled", label: "Scheduled" },
  { key: "progress", label: "In Process" },
];

// Shared style for the small outlined action buttons in the Generated Content rows
const listActionBtnStyle = {
  textTransform: "none" as const,
  fontSize: 12.5,
  fontWeight: 500,
  color: "rgba(255,255,255,0.8)",
  borderRadius: "8px",
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.02)",
  px: 1.5,
  py: 0.5,
  minWidth: "auto",
  "&:hover": {
    borderColor: "rgba(255,255,255,0.25)",
    background: "rgba(255,255,255,0.05)",
  },
};

// Single source of truth for the Generated Content table's column widths.
// The header row and every data row both reference this same template, so
// they're always perfectly aligned — no more independently-guessed widths
// (and no missing thumbnail column in the header) causing the zig-zag.
// Columns: thumbnail | content type | platform | title | status | created | actions
const GENERATED_CONTENT_GRID_TEMPLATE = "64px 150px 160px minmax(0,1fr) 80px 80px 400px";

// Style for the highlighted "Publish" button in each row
const listPublishBtnStyle = {
  textTransform: "none" as const,
  fontSize: 12.5,
  fontWeight: 600,
  color: "#fff",
  borderRadius: "8px",
  background: "#7C3AED",
  px: 1.5,
  py: 0.5,
  minWidth: "auto",
  boxShadow: "none",
  "&:hover": { background: "#6D28D9", boxShadow: "none" },
  "&.Mui-disabled": { background: "#7C3AED", opacity: 0.6, color: "#fff" },
};

// Parse a numeric price out of a free-form pricing string like "$499 USD",
// "USD 1,299", "$99.50/mo", etc. Returns 0 if no digits found.
const parsePriceFromString = (pricing: any): number => {
  if (pricing == null) return 0;
  const m = String(pricing).match(/[\d,]+(?:\.\d+)?/);
  if (!m) return 0;
  const n = Number(m[0].replace(/,/g, ""));
  return isNaN(n) ? 0 : n;
};

// Short relative time like "Tomorrow 9:00 AM" — mirrors the design's
// scheduled-time labels. Falls back to absolute date for far-out schedules.
// schedule_time may arrive from the API without a timezone suffix (e.g.
// "2025-05-15T14:00:00"), which is UTC but would otherwise be misread by
// `new Date()` as local time. Treat it as UTC when no offset is present,
// then let toLocaleString/toLocaleTimeString convert it to the browser's
// local timezone for display.
const formatScheduleShort = (iso: string): string => {
  if (!iso) return "";
  const hasTz = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso);
  const normalized = hasTz ? iso : iso + "Z";
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const tomorrowEnd = new Date(tomorrow.getTime() + dayMs);
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const isTomorrow = d >= tomorrow && d < tomorrowEnd;
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (isToday) return `Today ${time}`;
  if (isTomorrow) return `Tomorrow ${time}`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + ` ${time}`;
};

export const PLATFORM_CONFIG = {
  course: { icon: <SchoolOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />, bg: "#0A66C2", label: "Course" },
  linkedin: { icon: <LinkedInIcon sx={{ fontSize: 18, color: "#fff" }} />, bg: "#0A66C2", label: "LinkedIn" },
  twitter: { icon: <TwitterIcon sx={{ fontSize: 18, color: "#fff" }} />, bg: "#1DA1F2", label: "Twitter" },
  facebook: { icon: <FacebookIcon sx={{ fontSize: 18, color: "#fff" }} />, bg: "#1877F2", label: "Facebook" },
  youtube: { icon: <YouTubeIcon sx={{ fontSize: 18, color: "#fff" }} />, bg: "#FF0000", label: "YouTube" },
  authority: {
    icon: (
      <Box
        component="img"
        src={OceshaLogoIco}
        alt="Ocesha"
        sx={{
          //width: 20,
          //height: 20,
          objectFit: "contain",
          borderRadius: "50%",
        }}
      />
    ),
    color: "#FFFFFF",
    bg: "#000000",
    label: "Authority",
  },
};

type PlatformType = "linkedin" | "twitter" | "youtube" | "facebook" | "authority" | "course";

interface PlatformBadgesProps {
  platforms: string;
}

const PlatformBadges = ({ platforms }: PlatformBadgesProps) => {
  const parsedPlatforms = platforms
    ?.split("|")
    .map((p: string) => p.trim().toLowerCase())
    .filter((p: string) => p && p !== "both") as PlatformType[];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "nowrap",
      }}
    >
      {parsedPlatforms.map((platform: PlatformType) => {
        const config = PLATFORM_CONFIG[platform];

        if (!config) return null;

        return (
          <Tooltip title={config.label} arrow key={platform}>
            <Box
              key={platform}
              sx={{
                width: 34, height: 34, borderRadius: "8px",
                background: config.bg,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `0 2px 10px ${config.bg}66`,
                flexShrink: 0,
                transition: "transform 0.15s ease",
                "&:hover": { transform: "translateY(-2px)" },
              }}
            >
              {config.icon}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════
const AuthorityEngine: React.FC = () => {
  const navigate = useNavigate();
  const userEmail = localStorage.getItem("user_email") || "";
  const [digitalTwinPrompt, setDigitalTwinPrompt] = useState("");
  
  const { startTracking } = useCourseGeneration();

  // Reads the (still-fresh, <24h) saved digital twin prompt straight from
  // localStorage, synchronously. Used anywhere we need the CURRENT prompt
  // value without waiting for the `digitalTwinPrompt` state to catch up —
  // state updates from a mount-time useEffect don't apply until the next
  // render, so any other mount-time effect that reads `digitalTwinPrompt`
  // (e.g. via a `prompt = digitalTwinPrompt` default param) can still see
  // the initial "" on the very first fetch. Reading storage directly avoids
  // that race.
  const readStoredDigitalTwinPrompt = (): string => {
  try {
    const data = localStorage.getItem("digital_twin_prompt");
    if (data) {
      const parsed = JSON.parse(data);
      const isFresh = Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000;
      if (isFresh && parsed.text) return parsed.text;
    }
  } catch {
    // fall through to course_creation_topic fallback below
  }
  // digital_twin_prompt missing/expired/malformed — fall back to the
  // course creation topic so the suggestion APIs still get a prompt.
  var course_creation_topic = localStorage.getItem("course_creation_topic");
  return course_creation_topic || "" ;
};

  useEffect(() => {
    setDigitalTwinPrompt(readStoredDigitalTwinPrompt());
  }, []);

  const [openCustomize, setOpenCustomize] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  // Modal state for "video clip post requires upload" info popup
  const [openClipInfoModal, setOpenClipInfoModal] = useState(false);

  // ── Welcome walkthrough popup ──
  // Shows once per user (gated by localStorage flag). Remove the localStorage
  // check in the initializer if you want it to show on every visit instead.
  const [openWelcome, setOpenWelcome] = useState(
    () => localStorage.getItem("ocesha_welcome_seen") !== "1"
  );
  const [welcomePlaying, setWelcomePlaying] = useState(false);

  const closeWelcome = () => {
    localStorage.setItem("ocesha_welcome_seen", "1");
    setOpenWelcome(false);
    setWelcomePlaying(false);
  };

  const { data: platformStats, isLoading: statsLoading } = useGetPlatformStatusCountQuery(userEmail);
  const [triggerSuggestions] = useAuthorityEngineSuggestionsTriggerMutation();
  const [getTaskStatus] = useLazyGetTaskStatusQuery();

  // parsedCache is STATE so we can clear it on refresh. Versioned + TTL so stale
  // caches (old deployments or older than 1 day) get auto-discarded.
  const [parsedCache, setParsedCache] = useState<any>(() => {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) {
      // nothing current (e.g. the language was changed on another page): this language's own set
      const own = readLangCache(getContentLanguage());
      if (own) localStorage.setItem(CACHE_KEY, JSON.stringify(own));
      return own;
    }
    try {
      const parsed = JSON.parse(raw);

      // Version check — discard caches from older deployments
      if (parsed?.__version !== CACHE_VERSION) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }

      // 1-day expiry — discard stale caches so data re-fetches
      if (!parsed?.__cachedAt || (Date.now() - parsed.__cachedAt) > CACHE_TTL_MS) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }

      // Suggestions in another language: keep them aside (they are translated below,
      // in the fetch-on-mount effect) and use this language's own set if there is one
      if ((parsed?.__lang || "en") !== getContentLanguage()) {
        stashCurrentCache();
        localStorage.removeItem(CACHE_KEY);
        const own = readLangCache(getContentLanguage());
        if (own) localStorage.setItem(CACHE_KEY, JSON.stringify(own));
        return own;
      }

      return parsed;
    } catch {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  });

  // console.log("[AuthorityEngine] parsedCache on init:", parsedCache);

  const [authorityData, setAuthorityData] = useState<any>(parsedCache?.authorityData || null);
  const [isAuthorityLoading, setIsAuthorityLoading] = useState(!parsedCache?.authorityData);
  const [error, setError] = useState<any>(null);

  const [blogTriggerSuggestions, setBlogTriggerSuggestions] = useState<any[]>(
    parsedCache?.blogTriggerSuggestions || []
  );
  const [isBlogTriggerLoading, setIsBlogTriggerLoading] = useState(
    !parsedCache?.blogTriggerSuggestions?.length
  );

  const [newCourseSuggestions, setNewCourseSuggestions] = useState<any[]>(
    parsedCache?.newCourseSuggestions || []
  );
  const [isNewCourseLoading, setIsNewCourseLoading] = useState(false);

  // Course suggestion: array (was single object before). Coerce stale cache
  // entries that might hold a single object back into an array.
  const [randomCourseSuggestion, setRandomCourseSuggestion] = useState<any[]>(
    Array.isArray(parsedCache?.randomCourseSuggestion)
      ? parsedCache.randomCourseSuggestion
      : []
  );
  const [isRandomCourseLoading, setIsRandomCourseLoading] = useState(
    !Array.isArray(parsedCache?.randomCourseSuggestion) ||
    parsedCache.randomCourseSuggestion.length === 0
  );

  // User-editable price and difficulty for the course card
  const [coursePrice, setCoursePrice] = useState<number | string>(() => {
    const cached = parsedCache?.randomCourseSuggestion;
    if (Array.isArray(cached) && cached[0]?.pricing) {
      return parsePriceFromString(cached[0].pricing);
    }
    return "";
  });
  const [courseDifficulty, setCourseDifficulty] = useState<string>("beginner");
  const [isCourseSavingPushing, setIsCourseSavingPushing] = useState(false);

  // Video suggestions via axios + polling
  const [videoTriggerSuggestions, setVideoTriggerSuggestions] = useState<any[]>(
    parsedCache?.videoSuggestions || []
  );
  const [isVideoTriggerLoading, setIsVideoTriggerLoading] = useState(
    !parsedCache?.videoSuggestions?.length
  );

  // Show all recommendations by default; user can still collapse via the toggle.
  const [showAllRecommendations, setShowAllRecommendations] = useState(true);

  const genericSuggestions = Array.isArray(authorityData)
    ? authorityData
    : authorityData?.data?.suggested_content || [];

  const normalizedGenericSuggestions = genericSuggestions.map((item: any) => ({
    type: "generic",
    title: item.title, hook: item.hook,
    platform:
      item.type === "generic" || item.type === "video"
        ? ALL_PLATFORMS_WITH_AUTHORITY
        : Array.isArray(item.platform)
          ? item.platform[0]
          : item.platform,
    image_url: item.image_url, source_reference: item.source_reference,

    image_source: item.image_source,
    photographer_name: item.photographer_name,
    photographer_url: item.photographer_url,
    unsplash_image_id: item.unsplash_image_id,
    unsplash_attribution: item.unsplash_attribution,

    key_points: item.key_points || [], content_angle: item.content_angle,
    estimated_engagement: item.estimated_engagement,
    ...shoplineFields(item),   // SHOPLINE product suggestion: product id, url, images
  })) || [];

  const [recentActivityData, setRecentActivityData] = useState<any>({
    blogs: [], courses: [], posts: [], videos: [], images: []
  });

  const videoSuggestions = videoTriggerSuggestions;

  // ── Refs ──
  const isRefreshingRef = useRef(false);
  const isGeneratingRef = useRef(false);
  const generatedContentRef = useRef<HTMLDivElement | null>(null);

  // Courses saved in "faithful" knowledge_mode whose generation is still
  // pending. If one of these later fails (Posts.status -> "failed"), we
  // know to show the "no relevant data" message and clean up, rather than
  // the generic failure toast.
  const pendingFaithfulCourseIdsRef = useRef<Set<string>>(new Set());

  // ── Progressive status messaging (Recommended for You) ──────────────
  // refreshStartRef: timestamp handleRefresh() was called.
  // blogsReadyRef: timestamp blog/social/generic buckets all finished
  //   loading (i.e. course is the only bucket left). Reset to null at the
  //   start of every refresh; set once, the first time that state is true.
  const refreshStartRef = useRef<number | null>(null);
  const blogsReadyRef = useRef<number | null>(null);
  const [statusTick, setStatusTick] = useState(0); // bumped every second to force a re-render while loading

  // ── Cache helper ──────────────────────────────────────────────────
  // Merge a single bucket into the existing cache without disturbing the
  // others. This is the core of the "only fetch what's missing" behavior:
  // when a single bucket is re-fetched, only its slice is written, and the
  // other cached buckets are preserved. The original __cachedAt is kept so
  // the 1-day clock counts from the first write of the current cache, not
  // from each partial top-up. (handleRefresh clears the cache first, so a
  // refresh naturally starts a fresh timestamp.)
  const writeCacheBucket = (patch: Record<string, any>) => {
    let existing: any = {};
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) existing = JSON.parse(raw) || {};
    } catch {
      existing = {};
    }

    const merged = {
      ...existing,
      ...patch,
      __version: CACHE_VERSION,
      __cachedAt: existing.__cachedAt || Date.now(),
      __lang: existing.__lang || getContentLanguage(),   // language the suggestions are in
    };

    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(merged));
      console.log("[AuthorityEngine] cache bucket written:", Object.keys(patch));
    } catch (e) {
      console.warn("[AuthorityEngine] cache write failed:", e);
    }
  };

  const scrollToGeneratedContent = () => {
    setTimeout(() => {
      generatedContentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const renderRecommendationCard = (item: any, idx: number) => {

    // console.log('item res from dashboard => ', item);

    const type = item.type?.toLowerCase();
    const config: any = {
      blog: {
        color: "linear-gradient(135deg,#2563EB,#3B82F6)",
        borderColor: "rgba(59,130,246,0.45)",
        whyBg: "linear-gradient(135deg, rgba(37,99,235,0.14), rgba(59,130,246,0.06))",
        labelColor: "#60A5FA", labelBg: "rgba(59,130,246,0.18)",
        label: "BLOG", button: "Generate Blog",
        icon: <ArticleOutlinedIcon sx={{ color: "#fff" }} />,
      },
      course: {
        color: "linear-gradient(135deg,#059669,#10B981)",
        borderColor: "rgba(16,185,129,0.45)",
        whyBg: "linear-gradient(135deg, rgba(5,150,105,0.14), rgba(16,185,129,0.06))",
        labelColor: "#34D399", labelBg: "rgba(16,185,129,0.18)",
        label: "COURSE", button: "Generate Course",
        icon: <SchoolOutlinedIcon sx={{ color: "#fff" }} />,
      },
      video: {
        color: "linear-gradient(135deg,#EA580C,#F97316)",
        borderColor: "rgba(249,115,22,0.45)",
        whyBg: "linear-gradient(135deg, rgba(234,88,12,0.14), rgba(249,115,22,0.06))",
        labelColor: "#FB923C", labelBg: "rgba(249,115,22,0.18)",
        label: "CLIP", button: "Generate Post",
        icon: <PlayCircleOutlinedIcon sx={{ color: "#fff" }} />,
      },
      generic: {
        color: "linear-gradient(135deg,#7C3AED,#9333EA)",
        borderColor: "rgba(147,51,234,0.45)",
        whyBg: "linear-gradient(135deg, rgba(124,58,237,0.14), rgba(147,51,234,0.06))",
        labelColor: "#C4B5FD", labelBg: "rgba(147,51,234,0.20)",
        label: "SOCIAL POST", button: "Generate Post",
        icon: <LanguageIcon sx={{ color: "#fff" }} />,
      },
    };
    const ui = config[type] || config.generic;

    const imageSrc = item.image_url
      ? /^(https?:)?\/\//i.test(item.image_url)
        ? item.image_url
        : `${process.env.REACT_APP_RETURN_DOMAIN_NAME}${item.image_url.startsWith("/") ? "" : "/"}${item.image_url}`
      : "";

    return (
      <Grid item xs={12} sm={6} md={4} key={`rec-${type}-${idx}`}>
        <Box sx={{
          background: "rgba(255,255,255,0.03)",
          border: `1px solid ${ui.borderColor || "rgba(255,255,255,0.08)"}`,
          borderRadius: "16px", height: "100%",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}>
          {item.image_url && (
            // <Box sx={{ position: "relative" }}>
            <Box component="img" src={imageSrc} alt={item.title}
              onError={(e: any) => { e.currentTarget.style.display = "none"; }}
              sx={{ width: "auto", height: "auto", maxHeight: "214px", objectFit: "cover", objectPosition: "top", display: "block", flexShrink: 0 }}
            />
          )}
          {/* {item.image_source === "unsplash"  && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    px: 1,
                    py: 0.5,
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0))",
                    fontSize: "11px",
                    color: "#fff",
                  }}
                >
                  Photo by{" "}
                  <a
                    href={`${item.photographer_url}?utm_source=ocesha&utm_medium=referral`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#fff", textDecoration: "underline" }}
                  >
                    {item.photographer_name}
                  </a>{" "}
                  on{" "}
                  <a
                    href="https://unsplash.com?utm_source=ocesha&utm_medium=referral"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#fff", textDecoration: "underline" }}
                  >
                    Unsplash
                  </a>
                </Box>
              )} */}
          {/* </Box> */}
          <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", flex: 1 }}>
            <Stack spacing={1.5} height="100%">
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1.2} sx={{ minWidth: 0 }}>
                  <Box sx={{
                    width: 38, height: 38, borderRadius: "50%", background: ui.color,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    {ui.icon}
                  </Box>
                  <Stack spacing={0.6}>
                    <Box sx={{
                      display: "inline-flex", alignItems: "center",
                      px: 1.2, py: 0.4, borderRadius: "6px",
                      background: ui.labelBg,
                      color: ui.labelColor,
                      fontSize: 12, fontWeight: 800,
                      letterSpacing: 0.8, lineHeight: 1,
                      textTransform: "uppercase", mt: 0.3,
                      width: "fit-content", whiteSpace: "nowrap",
                    }}>
                      {ui.label}
                    </Box>
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1, py: 0.35, borderRadius: "999px",
                      background: "rgba(132,204,22,0.12)", color: "#84CC16",
                      fontSize: 9, fontWeight: 700, width: "fit-content", whiteSpace: "nowrap",
                    }}>
                      <StarIcon sx={{ fontSize: 11 }} />
                      Recommended
                    </Box>
                  </Stack>
                </Stack>
              </Stack>

              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 17, lineHeight: 1.35 }}>
                {item.title || item.name}
              </Typography>

              <Typography sx={{
                color: "rgba(255,255,255,0.65)", fontSize: 13, lineHeight: 1.55, flex: 1,
                display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
              }}>
                {item.hook}
              </Typography>

              {isShoplineSuggestion(item) && (
                <Box sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
                  px: 1.2, py: 0.8, borderRadius: "10px",
                  background: "rgba(75,165,235,0.08)", border: "1px solid rgba(75,165,235,0.25)",
                }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, color: "#4BA5EB" }}>
                      FROM YOUR STORE
                    </Typography>
                    <Typography noWrap sx={{ fontSize: 12.5, color: "#fff", fontWeight: 600 }}>
                      {item.product_title}{item.product_price ? ` · ${item.product_price}` : ""}
                    </Typography>
                  </Box>
                  {item.product_url && (
                    <Box component="a" href={item.product_url} target="_blank" rel="noopener noreferrer"
                      sx={{ fontSize: 12, color: "#4BA5EB", whiteSpace: "nowrap", textDecoration: "none",
                            "&:hover": { textDecoration: "underline" } }}>
                      View product ↗
                    </Box>
                  )}
                </Box>
              )}

              <Stack direction="row" spacing={1}>
                {[
                  {
                    icon: (
                      <Box
                        component="img"
                        src={OceshaLogoIco}
                        alt="Ocesha"
                        sx={{ objectFit: "contain", borderRadius: "50%" }}
                      />
                    ),
                    bg: "#000000",
                    label: "Authority",
                  },
                  { icon: <LinkedInIcon sx={{ fontSize: 18, color: "#fff" }} />, bg: "#0A66C2", label: "LinkedIn" },
                  { icon: <TwitterIcon sx={{ fontSize: 18, color: "#fff" }} />, bg: "#1DA1F2", label: "Twitter" },
                  { icon: <FacebookIcon sx={{ fontSize: 18, color: "#fff" }} />, bg: "#1877F2", label: "Facebook" },
                  { icon: <YouTubeIcon sx={{ fontSize: 18, color: "#fff" }} />, bg: "#FF0000", label: "YouTube" },
                ].map((s, i) => (
                  <Box
                    key={i}
                    title={s.label}
                    sx={{
                      width: 34, height: 34, borderRadius: "8px",
                      background: s.bg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: `0 2px 10px ${s.bg}66`,
                      flexShrink: 0,
                      transition: "transform 0.15s ease",
                      "&:hover": { transform: "translateY(-2px)" },
                    }}
                  >
                    {s.icon}
                  </Box>
                ))}
              </Stack>

              {type === "course" && (
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    type="number"
                    value={coursePrice}
                    onChange={(e) =>
                      setCoursePrice(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    placeholder="Price"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}>$</Typography>
                        </InputAdornment>
                      ),
                      sx: { color: "#fff", fontSize: 13, display: "none" },
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        background: "rgba(255,255,255,0.04)",
                        borderRadius: "8px",
                        "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
                        "&:hover fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                        "&.Mui-focused fieldset": { borderColor: "rgba(16,185,129,0.6)" },
                      },
                    }}
                  />
                  <FormControl size="small" sx={{ flex: 1, display: "none" }}>
                    <Select
                      value={courseDifficulty}
                      onChange={(e) => setCourseDifficulty(e.target.value)}
                      sx={{
                        background: "rgba(255,255,255,0.04)",
                        color: "#fff",
                        fontSize: 13,
                        borderRadius: "8px",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(16,185,129,0.6)" },
                        "& .MuiSelect-icon": { color: "rgba(255,255,255,0.5)" },
                      }}
                    >
                      <MenuItem value="beginner">Beginner</MenuItem>
                      <MenuItem value="intermediate">Intermediate</MenuItem>
                      <MenuItem value="advanced">Advanced</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              )}

              <Stack spacing={1}>
                {(() => {
                  const handleCardAction = () => {
                    if (type === "video") {
                      handleGenerate(item, `${type}-${idx}`);
                      //setOpenClipInfoModal(true);
                    }
                    else if (type === "generic") {
                      // Social post: generate via API, stay on page, jump to Generated Content
                      handleGenerate(item, `${type}-${idx}`);
                    }
                    else if (type === "blog") {
                      handleDirectGenerateBlog(item, `${type}-${idx}`);
                    }
                    else if (type === "course") {
                      handleSaveCourseAndPush(item, `${type}-${idx}`);
                    }
                    else {
                      handleGenerate(item, `${type}-${idx}`);
                    }
                  };

                  const handleCustomizeAction = () => {
                    if (type === "blog") {
                      setSelectedItem(item);
                      setOpenCustomize(true);
                    }
                    else if (type === "course") {
                      console.log("customize course item", item);
                      //handleGenerateCourse(item, `${type}-${idx}`);
                      setSelectedCourse(item);
                      setOpenCustomizeCourse(true);
                    } else if (type === "video") {
                      setOpenClipInfoModal(true);
                    }
                    else if (type === "generic") {
                      // Customize: navigate to publish-schedule-clip (generates en route)
                      handleNavigateToEdit(item, `${type}-${idx}`);
                    }
                    else {
                      handleNavigateToEdit(item, `${type}-${idx}`);
                    }
                  };

                  return (
                    <>
                      <GradientBtn
                        label={ui.button}
                        gradient={ui.color}
                        loading={loadingIndex === `${type}-${idx}`}
                        //loading={type !== "generic" && loadingIndex === `${type}-${idx}`}
                        onClick={handleCardAction}
                      />

                      <Button
                        fullWidth
                        disabled={type === "video"}
                        onClick={handleCustomizeAction}
                        startIcon={<TuneIcon sx={{ fontSize: 15 }} />}
                        sx={{
                          textTransform: "none",
                          color: "#ffffff",
                          fontSize: 13,
                          fontWeight: 600,
                          background: "rgba(160,160,160,0.08)",
                          border: "1px solid rgba(160,160,160,0.35)",
                          borderRadius: "10px",
                          py: 0.6,
                          "&:hover": {
                            background: type === "video" ? "rgba(160,160,160,0.08)" : "rgba(160,160,160,0.16)",
                            borderColor: type === "video" ? "rgba(160,160,160,0.35)" : "rgba(160,160,160,0.6)",
                            color: type === "video" ? "rgba(255,255,255,0.38)" : "#D0D0D0",
                          },
                          "&.Mui-disabled": {
                            color: "rgba(255,255,255,0.38)",
                            borderColor: "rgba(255,255,255,0.12)",
                            background: "transparent",
                            cursor: "not-allowed",
                            opacity: 0.5,
                          },
                        }}
                      >
                        Customize
                      </Button>
                    </>
                  );
                })()}
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Grid>
    );
  };

  // ── Helpers ──
  const parseTimestamp = (s: string | undefined): number => {
    if (!s) return 0;
    const hasTz = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(s);
    const iso = hasTz ? s : s + "Z";
    const t = new Date(iso).getTime();
    return isNaN(t) ? 0 : t;
  };
  const timeAgo = (isoString: string): string => {
    const ms = parseTimestamp(isoString);
    if (!ms) return "";
    const seconds = Math.floor((Date.now() - ms) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
  };
  const objectIdToDate = (id: string): string => {
    if (!id || id.length < 8) return "";
    try {
      const seconds = parseInt(id.substring(0, 8), 16);
      if (isNaN(seconds)) return "";
      return new Date(seconds * 1000).toISOString();
    } catch { return ""; }
  };
  const getPlatform = (platform?: string): string | null => {
    if (!platform) return null;
    const p = platform.toLowerCase();
    if (p.includes("linkedin")) return "linkedin";
    if (p.includes("twitter")) return "twitter";
    if (p.includes("facebook")) return "facebook";
    if (p.includes("youtube")) return "youtube";
    if (p.includes("blog")) return "blog";
    if (p.includes("course")) return "course";
    if (p.includes("authority")) return "authority";
    return null;
  };

  const isInProgress = (s?: string): boolean =>
    s === "in_progress" || s === "inprogress";

  // ── Refresh / cache ──
  // Refresh is the ONLY path that wipes the cache and re-fetches everything.
  // Normal navigation/reload keeps whatever valid cache exists and only fetches
  // the buckets that are missing (see the mount effect below).
  //
  // `options.content_types` (from the "Tell me what to create" popup) is
  // forwarded as-is to every trigger call — no client-side filtering. Each
  // bucket still gets triggered; the backend decides what to do with a given
  // type's quantity (including 0) and with `options.mode` ("recommend" just
  // fetches suggestions like today; "recommend_generate" asks the backend to
  // generate the content right away, if it supports that param).
  const handleRefresh = (
    prompt?: string,
    options?: {
      content_types?: { type: string; quantity: number }[];
      mode?: "recommend" | "recommend_generate";
      knowledge_mode?: "faithful" | "balanced" | "expanded";
    }
  ) => {
    console.log("[AuthorityEngine] refresh clicked — clearing cache and state");

    // Reset the progressive status-message timers for this refresh cycle.
    refreshStartRef.current = Date.now();
    blogsReadyRef.current = null;

    const activePrompt = prompt ?? readStoredDigitalTwinPrompt();

    setDigitalTwinPrompt(activePrompt);
    /*const stored = localStorage.getItem("digital_twin_prompt");

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setDigitalTwinPrompt(parsed.text || "");
      } catch {}
    }*/

    isRefreshingRef.current = true;

    // Clear the cache so the per-bucket writes below start a fresh timestamp.
    // (Saved sets in other languages belong to the old suggestions: drop them too.)
    localStorage.removeItem(CACHE_KEY);
    clearAllLangCaches();
    setParsedCache(null);

    // When called without explicit options (e.g. the header "Refresh" button,
    // which just does handleRefresh() with no args), fall back to whatever
    // content_types + mode the "Tell me what to create" popup last saved to
    // localStorage — same 24h-expiry pattern as the digital_twin_prompt text —
    // instead of silently omitting them from the payload.
    let content_types = options?.content_types;
    let mode = options?.mode;
    let knowledge_mode = options?.knowledge_mode;

    if (!options) {
      const stored = readStoredContentTypesAndMode();
      content_types = stored.content_types;
      mode = stored.mode;
      knowledge_mode = stored.knowledge_mode;
    }

    setAuthorityData(null);
    setGeneratedCourseSuggestion(null);
    setNewCourseSuggestions([]);
    setBlogTriggerSuggestions([]);
    setVideoTriggerSuggestions([]);
    setRandomCourseSuggestion([]);

    setIsAuthorityLoading(true);
    setIsBlogTriggerLoading(true);
    setIsNewCourseLoading(true);
    setIsVideoTriggerLoading(true);
    setIsRandomCourseLoading(true);

    // Re-fetch every bucket; each writes its own slice back to the cache.
    // content_types + mode are forwarded to every call so the backend has
    // full context regardless of which bucket it's for.
    fetchAuthoritySuggestions(activePrompt, mode, content_types, knowledge_mode);
    fetchCourseSuggestion(activePrompt, mode, content_types, knowledge_mode);
    fetchBlogSuggestionsTrigger(activePrompt, mode, content_types, knowledge_mode);
    fetchVideoSuggestionsTrigger(activePrompt, mode, content_types, knowledge_mode);
    
    // NOTE: "images" has no dedicated suggestions-trigger endpoint yet — wire
    // one in here (same trigger+poll pattern) once the backend exposes it.

    setTimeout(() => { isRefreshingRef.current = false; }, 0);

    if (userEmail) {
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      axios.get(`${apiUrl}/api/prompts/recent_activity?user_email=${encodeURIComponent(userEmail)}`)
        .then((res) => {
          if (res.data?.status === "success" && res.data?.data) setRecentActivityData(res.data.data);
        }).catch(() => { });
      axios.get(`${apiUrl}/api/prompts/authority_signals?user_email=${encodeURIComponent(userEmail)}`)
        .then((res) => {
          if (res.data?.status === "success" && res.data?.data) setAuthoritySignals(res.data.data);
        }).catch(() => { });
    }
  };

  // ── Language change → regenerate suggestions in the new language ──
  // Suggestion cards are AI-generated, so they are re-created in the selected language
  // (not machine-translated). Content already saved (Generated Content list, recent
  // activity) stays in the language it was written in.
  const handleRefreshRef = useRef(handleRefresh);
  handleRefreshRef.current = handleRefresh;

  // Show a suggestion set (from the per-language store or a translation)
  const applySuggestionCache = (c: any) => {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch { /* ignore */ }
    setParsedCache(c);
    setAuthorityData(c?.authorityData || null);
    setIsAuthorityLoading(false);
    setBlogTriggerSuggestions(c?.blogTriggerSuggestions || []);
    setIsBlogTriggerLoading(false);
    setNewCourseSuggestions(c?.newCourseSuggestions || []);
    setRandomCourseSuggestion(Array.isArray(c?.randomCourseSuggestion) ? c.randomCourseSuggestion : []);
    setIsRandomCourseLoading(false);
    setVideoTriggerSuggestions(c?.videoSuggestions || []);
    setIsVideoTriggerLoading(false);
  };

  /** true when the cards are now shown in `lang` (reused or translated), false -> generate */
  const showSuggestionsIn = async (lang: string): Promise<boolean> => {
    const own = readLangCache(lang);
    if (own) { applySuggestionCache(own); return true; }
    const source = findSourceCache(lang);
    if (!source) return false;
    setIsAuthorityLoading(true);
    setIsBlogTriggerLoading(true);
    setIsRandomCourseLoading(true);
    setIsVideoTriggerLoading(true);
    try {
      const translated = await translateSuggestionCache(source, lang);
      if (getContentLanguage() !== lang) return true;   // switched again meanwhile
      try { localStorage.setItem(LANG_CACHE_KEY(lang), JSON.stringify(translated)); } catch { /* ignore */ }
      applySuggestionCache(translated);
      return true;
    } catch (e) {
      console.warn("[AuthorityEngine] translating suggestions failed, regenerating", e);
      return false;
    }
  };
  const showSuggestionsInRef = useRef(showSuggestionsIn);
  showSuggestionsInRef.current = showSuggestionsIn;

  useEffect(() => {
    let timer: any = null;
    const onLanguageChanged = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        stashCurrentCache();          // keep the set of the language we are leaving
        const ok = await showSuggestionsInRef.current(getContentLanguage());
        if (!ok) handleRefreshRef.current();
      }, 300);
    };
    window.addEventListener("content-language-changed", onLanguageChanged);
    return () => { clearTimeout(timer); window.removeEventListener("content-language-changed", onLanguageChanged); };
  }, []);

  // ── Mutations ──
  const [generatePost] = useGeneratePostMutation();
  const [loadingIndex, setLoadingIndex] = useState<string | null>(null);
  const [getUserDetailsMutation] = useGetUserDetailsMutation();

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [sort, setSort] = useState("recent");
  const [activeTab, setActiveTab] = useState("all");
  const [paginationLoadingKey, setPaginationLoadingKey] = useState<string | number | null>(null);
  const { data: initiatedData, isLoading: initiatedLoading, isFetching: initiatedFetching, refetch } = useGetInitiatedPostsQuery(
    { email: userEmail, page, limit },
    { refetchOnMountOrArgChange: true }
  );
  const initiatedPosts = initiatedData?.data || [];
  const total = initiatedData?.total || 0;
  const totalPages = Math.ceil(total / limit);

  // Clear the pagination spinner once the new page's data has finished fetching
  useEffect(() => {
    if (!initiatedFetching) {
      setPaginationLoadingKey(null);
    }
  }, [initiatedFetching]);

  const [activatePost] = useActivatePostMutation();
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [updatePost, { isLoading: updating }] = useUpdatePostMutation();
  const [openEditor, setOpenEditor] = useState(false);
  const [editingPost, setEditingPost] = useState<any>(null);
  const [editedContent, setEditedContent] = useState("");
  const [editButtonLoadingId, setEditButtonLoadingId] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<any>();

  const [openSchedule, setOpenSchedule] = useState(false);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [schedulePost, { isLoading: scheduling }] = useSchedulePostMutation();
  const [scheduleMode, setScheduleMode] = useState<"schedule" | "reschedule">("schedule");

  // ── Cancel scheduled post ─────────────────────────────────────────────
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [postToCancel, setPostToCancel] = useState<any>(null);
  const [cancelling, setCancelling] = useState(false);
  const handleCancelScheduledPost = async () => {
    if (!postToCancel) return;
    setCancelling(true);
    try {
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const postId = postToCancel.id || postToCancel._id;
      await axios.post(
        `${apiUrl}/api/socialmedia/api/posts/${postId}/cancel`
      );
      toast.success("Scheduled post cancelled");
      setOpenCancelConfirm(false);
      setPostToCancel(null);
      refetch();
    } catch {
      toast.error("Couldn't cancel the scheduled post. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  // ── Open the publish / schedule screen for a post (used by "Edit" and "Publish") ──
  const goToPublishSchedule = (post: any) => {
    navigate("/ocesha/publish-schedule-clip", {
      state: {
        postContent: post.content || "",
        postId: post.id || post._id || "",
        clipTitle: post.title || post.content?.substring(0, 60) || "",
        clipThumbnail: post.image_url || "",
        platform: post.platform || "",
        blogId: post.blog_id || "",
        sourceType: post.type || "",
        video_url: post.video_url || "",
        source_url: post.source_url || "",
        scheduleTime: post.schedule_time || "",
        cta_url: post.cta_url || "",
        cta_label: post.cta_label || "",
        product_images: post.shopline_product?.images || [],
        image_urls: post.image_urls || [],
      },
    });
  };

  // ── Unpublish a published post ───────────────────────────────────────
  // Always removes it from the public profile page. If the post was also published
  // to social platforms the user can choose to remove it from there too.
  const [openUnpublishConfirm, setOpenUnpublishConfirm] = useState(false);
  const [postToUnpublish, setPostToUnpublish] = useState<any>(null);
  const [removeFromSocial, setRemoveFromSocial] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);

  const getSocialPlatformsOf = (post: any): string[] =>
    String(post?.platform || "")
      .split("|")
      .map((p) => p.trim().toLowerCase())
      .filter((p) => ["linkedin", "twitter", "facebook", "youtube"].includes(p));

  // Social platforms the post is really live on. A platform whose publish failed (or that was
  // already removed) is not live; posts without per-platform results are treated as live.
  const getLiveSocialPlatforms = (post: any): string[] =>
    getSocialPlatformsOf(post).filter((p) => {
      const r = post?.social_results?.[p];
      return !r || r.status === "published";
    });

  // Unpublish only makes sense when the post is live on the profile page or on a social platform.
  const isPublishedAnywhere = (post: any): boolean => {
    if (!post) return false;
    // Blogs and courses are live when the blog / course itself is published
    // (the list tells us through `source_published`).
    const kind = String(post.type || "").toLowerCase();
    if ((kind === "blog" || kind === "course") && post.content_id && post.source_published) return true;
    if (post.status !== "published") return false;
    const hasProfile = String(post.platform || "")
      .split("|")
      .some((p) => p.trim().toLowerCase() === "authority");
    return hasProfile || getLiveSocialPlatforms(post).length > 0;
  };

  const platformLabel = (p: string) =>
    p === "twitter" ? "X (Twitter)" : p.charAt(0).toUpperCase() + p.slice(1);

  const handleUnpublishPost = async () => {
    if (!postToUnpublish) return;
    setUnpublishing(true);
    try {
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const postId = postToUnpublish.id || postToUnpublish._id;
      const res = await axios.post(
        `${apiUrl}/api/socialmedia/api/posts/${postId}/unpublish`,
        null,
        { params: { user_email: userEmail, remove_from_social: removeFromSocial } }
      );

      const social = res?.data?.social || {};
      const failed = Object.entries(social).filter(([, v]: any) => v?.status === "failed");

      if (failed.length === 0) {
        toast.success(
          removeFromSocial && Object.keys(social).length
            ? "Unpublished from your profile and social platforms"
            : "Unpublished from your profile page"
        );
      } else {
        toast.success("Unpublished from your profile page");
        failed.forEach(([plat, v]: any) =>
          toast.warn(v?.message || `Could not remove the post from ${platformLabel(plat)}`)
        );
      }

      setOpenUnpublishConfirm(false);
      setPostToUnpublish(null);
      setRemoveFromSocial(false);
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Couldn't unpublish the post. Please try again.");
    } finally {
      setUnpublishing(false);
    }
  };

  const [generatedCourseSuggestion, setGeneratedCourseSuggestion] = useState<any>(null);
  const [authoritySignals, setAuthoritySignals] = useState<any>({
    linkedin: 0, twitter: 0, youtube: 0, facebook: 0,
    total_posts: 0, authority_score: 0,
  });
  const [upgradeModal, setUpgradeModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const genieSelector = useAppSelector((state) => state.genieState);
  const [getProduct] = useLazyGetProductQuery();

  const selectedCampaign =
    localStorage.getItem("campId") ||
    localStorage.getItem("publishingCampId") ||
    "";

  const { audioRef } = useTTSQueue();

  const [openCustomizeCourse, setOpenCustomizeCourse] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<any>(null);

  // ── Poll a just-saved course's generation status, and clean up automatically
  // if it failed specifically because faithful mode found no matching content.
  // courseId: the _id returned by /save-course.
  // knowledgeMode: the mode that was actually sent for this course, so we only
  // show the "switch mode" message when faithful mode was the culprit.
  const pollCourseGenerationForFailure = async (courseId: string, knowledgeMode: string) => {
    if (!courseId) return;
    const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
    const MAX_ATTEMPTS = 40;       // ~4 minutes at 6s intervals — course generation is the slow bucket
    const POLL_INTERVAL_MS = 6000;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      try {
        const res = await axios.post(
          `${apiUrl}/api/prompts/course_status`,
          null,
          { params: { course_id: courseId }, headers: { accept: "application/json" } }
        );
        const { status, error } = res.data || {};

        if (status === "ready" || status === "completed" || status === "generated") {
          return; // generated fine — nothing to do
        }

        if (status === "failed") {
          if (error === "no data found" && knowledgeMode === "faithful") {
            toast.error(
              "No relevant data found in your uploaded content. Please choose a different mode rather than Faithful."
            );
          } else {
            toast.error("Course generation failed. Please try again.");
          }

          try {
            await axios.post(`${apiUrl}/api/prompts/delete_post_and_course`, null, {
              params: { course_id: courseId },
            });
          } catch (delErr) {
            console.error("Failed to delete incomplete course:", delErr);
          }

          refetch();
          notifyCreditsChanged();
          return;
        }
        // status is "inprogress"/"initiated"/etc. — keep polling
      } catch (err) {
        console.error("[pollCourseGenerationForFailure] status check failed:", err);
      }
    }

    console.warn(`[pollCourseGenerationForFailure] gave up polling course ${courseId} after ${MAX_ATTEMPTS} attempts`);
  };

  // ── API fetchers ──
  const pollTaskStatus = async (taskId: string) => {
    let attempts = 0;
    const MAX_ATTEMPTS = 120;
    while (attempts < MAX_ATTEMPTS) {
      attempts++;
      try {
        const res = await getTaskStatus(taskId).unwrap();
        if (res.state === "done") return res.result;
        if (res.state === "failed") throw new Error("Suggestions failed");
      } catch (err) { console.error("Polling error:", err); }
      await new Promise((r) => setTimeout(r, 5000));
    }
    throw new Error("Timeout waiting for suggestions");
  };

  const fetchAuthoritySuggestions = async (
    prompt = digitalTwinPrompt,
    mode?: "recommend" | "recommend_generate",
    content_types?: { type: string; quantity: number }[],
    knowledge_mode?: "faithful" | "balanced" | "expanded"
  ) => {
    const requestLang = getContentLanguage();   // ignore results if the language changes meanwhile
    if (!userEmail) return;
    try {
      setIsAuthorityLoading(true);
      setError(null);
      const triggerRes = await triggerSuggestions({
        user_email: userEmail,
        digital_twin_prompt: prompt,
        ...(mode ? { mode } : {}),
        ...(content_types ? { content_types } : {}),
        ...(knowledge_mode ? { knowledge_mode } : {}),
      }).unwrap();
      const result = await pollTaskStatus(triggerRes.task_id);
      if (requestLang !== getContentLanguage()) return;
      setAuthorityData(result);
      // Cache just this bucket, merged onto whatever else is already cached.
      writeCacheBucket({ authorityData: result });
    } catch (err) {
      console.error(err); setError(err);
    } finally {
      if (requestLang === getContentLanguage()) setIsAuthorityLoading(false);   // a newer request (new language) owns the spinner
    }
  };

  const fetchCourseSuggestion = async (
    prompt = digitalTwinPrompt,
    mode?: "recommend" | "recommend_generate",
    content_types?: { type: string; quantity: number }[],
    knowledge_mode?: "faithful" | "balanced" | "expanded"
  ) => {
    const requestLang = getContentLanguage();   // ignore results if the language changes meanwhile
    const courseId = localStorage.getItem("course_id") || "";
    if (!userEmail || productOnlyMode()) {
      setIsRandomCourseLoading(false);
      return;
    }
    try {
      setIsRandomCourseLoading(true);
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const triggerRes = await axios.post(
        `${apiUrl}/api/prompts/process_create_courses_topics_suggestions_trigger`,
        { ...(content_types ? { content_types } : {}) },
        {
          params: { user_email: userEmail, course_id: courseId, result_count: 3, digital_twin_prompt: prompt, ...(mode ? { mode } : {}), ...(knowledge_mode ? { knowledge_mode } : {}),},
          headers: { accept: "application/json" },
        }
      );

      const taskId = triggerRes.data?.task_id;
      if (!taskId) return;
      const result = await pollTaskStatus(taskId);
      if (requestLang !== getContentLanguage()) return;

      let list: any[] = [];
      if (Array.isArray(result?.courses)) list = result.courses;
      else if (Array.isArray(result?.data?.courses)) list = result.data.courses;
      else if (Array.isArray(result?.course_suggestions)) list = result.course_suggestions;
      else if (Array.isArray(result?.data?.course_suggestions)) list = result.data.course_suggestions;
      else if (Array.isArray(result)) list = result;

      if (!Array.isArray(list) || list.length === 0) {
        console.warn("[CourseSuggestion] empty or unrecognized list shape:", result);
        return;
      }

      // Normalize ALL returned courses (was: pick one at random)
      const normalizedList = list.map((picked: any) => ({
        type: "course",
        title: picked.title,
        hook: picked.description,
        platform: [],
        image_url: picked.image_url || `https://picsum.photos/seed/${encodeURIComponent(picked.parent_course_id || picked.name || "course")}/400/200`,
        image_url_small: picked.thumbnail || picked.image_url,

        image_source: picked.image_source,
        photographer_name: picked.photographer_name,
        photographer_url: picked.photographer_url,
        unsplash_image_id: picked.unsplash_image_id,
        unsplash_attribution: picked.unsplash_attribution,

        source_reference: picked.description || "",
        key_points: picked.learning_outcomes || [],
        content_angle: picked.target_audience || "",
        estimated_engagement: "",
        parent_course_id: picked.parent_course_id,
        course_number: picked.course_number,
        name: picked.name,
        description: picked.description,
        target_audience: picked.target_audience,
        duration: picked.duration,
        pricing: picked.pricing,
        learning_outcomes: picked.learning_outcomes,
        curriculum: picked.curriculum,
      }));

      // Persist the FIRST one for handleSaveCourseAndPush fallback
      try {
        localStorage.setItem("course_suggestion_selected", JSON.stringify(list[0]));
      } catch (e) {
        console.warn("Could not persist course_suggestion_selected:", e);
      }

      setRandomCourseSuggestion(normalizedList);
      setCoursePrice(parsePriceFromString(list[0]?.pricing));
      setCourseDifficulty("beginner");
      // Cache just this bucket.
      writeCacheBucket({ randomCourseSuggestion: normalizedList });
    } catch (err) {
      console.error("[CourseSuggestion] fetch failed:", err);
    } finally {
      if (requestLang === getContentLanguage()) setIsRandomCourseLoading(false);   // a newer request (new language) owns the spinner
    }
  };

  const fetchNewCourseSuggestion = async () => {
    const requestLang = getContentLanguage();   // ignore results if the language changes meanwhile
    if (!userEmail) return;
    if (productOnlyMode()) { setIsNewCourseLoading(false); return; }
    try {
      setIsNewCourseLoading(true);

      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const courseId = localStorage.getItem("course_id") || "";
      const triggerRes = await axios.post(
        `${apiUrl}/api/prompts/process_new_course_suggestions_trigger`,
        { user_email: userEmail, course_id: courseId, result_count: 2, digital_twin_prompt: digitalTwinPrompt, },
        { headers: { "Content-Type": "application/json", accept: "application/json" } }
      );

      const taskId = triggerRes.data?.task_id;
      if (!taskId) {
        console.warn("[NewCourseSuggestion] no task_id returned:", triggerRes.data);
        return;
      }
      const result = await pollTaskStatus(taskId);
      if (requestLang !== getContentLanguage()) return;

      let rawList: any[] = [];
      if (Array.isArray(result?.data)) rawList = result.data;
      else if (Array.isArray(result?.data?.courses)) rawList = result.data.courses;
      else if (Array.isArray(result?.data?.course_suggestions)) rawList = result.data.course_suggestions;
      else if (Array.isArray(result?.courses)) rawList = result.courses;
      else if (Array.isArray(result?.course_suggestions)) rawList = result.course_suggestions;
      else if (Array.isArray(result)) rawList = result;

      if (rawList.length === 0) {
        console.warn("[NewCourseSuggestion] empty or unrecognized list shape:", result);
        return;
      }

      const normalized = rawList.map((item: any) => ({
        type: "course",
        course_id: item.course_id,
        title: item.title,
        hook: item.hook,
        platform: item.platforms || item.platform || [],
        image_url: item.image_url,
        image_url_small: item.thumbnail || item.image_url,
        source_reference: item.prerequisites || item.source_reference || "",
        key_points: item.learning_outcomes || item.key_points || [],
        content_angle: item.category || item.content_angle || "",
        estimated_engagement: item.engagement_potential || item.estimated_engagement || "",
        category: item.category,
        target_audience: item.target_audience,
        difficulty_level: item.difficulty_level,
        estimated_duration: item.estimated_duration,
        suggested_modules: item.suggested_modules,
        prerequisites: item.prerequisites,
        tags: item.tags,
      }));

      setNewCourseSuggestions(normalized);
      // Cache just this bucket.
      writeCacheBucket({ newCourseSuggestions: normalized });

      // Persist the first course as "selected" so AuthorityEngine's save-course
      // handler can build the payload without re-fetching.
      if (normalized.length > 0) {
        try {
          // Use the raw item (with parent_course_id etc.) not the normalized one
          localStorage.setItem("course_suggestion_selected", JSON.stringify(rawList[0]));
        } catch (e) {
          console.warn("Could not persist course_suggestion_selected:", e);
        }
      }

    } catch (err) {
      console.error("[NewCourseSuggestion] fetch failed:", err);
    } finally {
      if (requestLang === getContentLanguage()) setIsNewCourseLoading(false);   // a newer request (new language) owns the spinner
    }
  };

  const fetchBlogSuggestionsTrigger = async (
    prompt = digitalTwinPrompt,
    mode?: "recommend" | "recommend_generate",
    content_types?: { type: string; quantity: number }[],
    knowledge_mode?: "faithful" | "balanced" | "expanded"
  ) => {
    const requestLang = getContentLanguage();   // ignore results if the language changes meanwhile
    if (!userEmail) return;
    try {
      setIsBlogTriggerLoading(true);
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const triggerRes = await axios.post(
        `${apiUrl}/api/prompts/process_create_new_blog_suggestions_trigger`,
        { user_email: userEmail, result_count: 2, digital_twin_prompt: prompt, ...(mode ? { mode } : {}), ...(content_types ? { content_types } : {}), ...(knowledge_mode ? { knowledge_mode } : {}), },
        { headers: { "Content-Type": "application/json", accept: "application/json" } }
      );

      const taskId = triggerRes.data?.task_id;
      if (!taskId) return;
      const result = await pollTaskStatus(taskId);
      if (requestLang !== getContentLanguage()) return;
      const list = result?.data?.blog_suggestions ||
        (Array.isArray(result?.data) && result.data) ||
        result?.blog_suggestions ||
        (Array.isArray(result) ? result : []);
      // console.log('list response of blog=>', list);
      if (!Array.isArray(list) || list.length === 0) return;
      const normalized = list.map((item: any) => ({
        type: "blog", blog_id: item.blog_id, title: item.title,
        hook: item.hook || item.description || "",
        platform: (item.platforms || []).map((p: string) => (typeof p === "string" ? p.toLowerCase() : p)),
        image_url: item.image_url || "https://picsum.photos/400/200",
        image_url_small: item.thumbnail || item.image_url,
        image_source: item.image_source,
        photographer_name: item.photographer_name,
        photographer_url: item.photographer_url,
        unsplash_image_id: item.unsplash_image_id,
        unsplash_attribution: item.unsplash_attribution,
        source_reference: item.source_reference || "",
        key_points: item.key_points || [],
        content_angle: item.content_angle || item.category || "",
        estimated_engagement: item.estimated_engagement || item.engagement_potential || "",
        category: item.category, target_audience: item.target_audience,
        estimated_read_time: item.estimated_read_time, tags: item.tags,
        ...shoplineFields(item),   // SHOPLINE product blog: product id, url, images
      }));
      setBlogTriggerSuggestions(normalized);
      // Cache just this bucket.
      writeCacheBucket({ blogTriggerSuggestions: normalized });
    } catch (err) { console.error("[BlogSuggestions] fetch failed:", err); }
    finally { if (requestLang === getContentLanguage()) setIsBlogTriggerLoading(false); }
  };

  const fetchVideoSuggestionsTrigger = async (
    prompt = digitalTwinPrompt,
    mode?: "recommend" | "recommend_generate",
    content_types?: { type: string; quantity: number }[],
    knowledge_mode?: "faithful" | "balanced" | "expanded"
  ) => {
    const requestLang = getContentLanguage();   // ignore results if the language changes meanwhile
    if (!userEmail) return;
    if (productOnlyMode()) { setIsVideoTriggerLoading(false); return; }
    try {
      setIsVideoTriggerLoading(true);
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const triggerRes = await axios.post(
        `${apiUrl}/api/prompts/process_video_suggestions_trigger`,
        { user_email: userEmail, result_count: 2, digital_twin_prompt: prompt, ...(mode ? { mode } : {}), ...(content_types ? { content_types } : {}), ...(knowledge_mode ? { knowledge_mode } : {}), },
        { headers: { "Content-Type": "application/json", accept: "application/json" } }
      );

      const taskId = triggerRes.data?.task_id;
      if (!taskId) {
        console.warn("[VideoSuggestions] no task_id returned:", triggerRes.data);
        return;
      }
      const result = await pollTaskStatus(taskId);
      if (requestLang !== getContentLanguage()) return;

      let rawList: any[] = [];
      if (Array.isArray(result?.data?.video_suggestions)) rawList = result.data.video_suggestions;
      else if (Array.isArray(result?.video_suggestions)) rawList = result.video_suggestions;
      else if (Array.isArray(result?.data?.videos)) rawList = result.data.videos;
      else if (Array.isArray(result?.videos)) rawList = result.videos;
      else if (Array.isArray(result?.data)) rawList = result.data;
      else if (Array.isArray(result)) rawList = result;

      if (!Array.isArray(rawList) || rawList.length === 0) {
        console.warn("[VideoSuggestions] empty or unrecognized list shape:", result);
        return;
      }

      const normalized = rawList.map((item: any) => ({
        type: "video",
        title: item.title,
        hook: item.hook || item.description || "",
        platform: item.platforms || item.platform || [],
        image_url: item.thumbnail || item.image_url,
        image_url_small: item.thumbnail || item.image_url,
        image_source: item.image_source,
        photographer_name: item.photographer_name,
        photographer_url: item.photographer_url,
        unsplash_image_id: item.unsplash_image_id,
        unsplash_attribution: item.unsplash_attribution,
        source_reference: item.media_url || item.source_reference || "",
        key_points: item.key_points || [],
        content_angle: item.content_angle || item.category || "",
        estimated_engagement: item.estimated_engagement || item.engagement_potential || "",
        media_url: item.media_url,
        video_id: item.video_id,
        tags: item.tags,
      }));

      setVideoTriggerSuggestions(normalized);
      // Cache just this bucket.
      writeCacheBucket({ videoSuggestions: normalized });
    } catch (err) {
      console.error("[VideoSuggestions] fetch failed:", err);
    } finally {
      if (requestLang === getContentLanguage()) setIsVideoTriggerLoading(false);   // a newer request (new language) owns the spinner
    }
  };

  const fetchData = async () => {
    try {
      const response = await getUserDetailsMutation({
        email: userEmail || getCookie("user_email") || "",
        filter: "", start_date: "", end_date: ""
      });
      if ("data" in response) setUserDetails(response.data.data);
      if ("data" in response) console.log(response.data.data);
    } catch (error) { console.log(error); }
  };

  // ── Effects ──
  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!userEmail) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "profile_identified",
      user_email: userEmail,
      page: "authority_engine",
    });
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) return;
    const fn = async () => {
      try {
        const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
        const res = await axios.get(`${apiUrl}/api/prompts/authority_signals?user_email=${encodeURIComponent(userEmail)}`);
        if (res.data?.status === "success" && res.data?.data) setAuthoritySignals(res.data.data);
      } catch (err) { console.error(err); }
    };
    fn();
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) return;
    const fn = async () => {
      try {
        const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
        const res = await axios.get(`${apiUrl}/api/prompts/recent_activity?user_email=${encodeURIComponent(userEmail)}`);
        if (res.data?.status === "success" && res.data?.data) setRecentActivityData(res.data.data);
      } catch (err) { console.error(err); }
    };
    fn();
  }, [userEmail]);

  // ── Fetch-on-mount, gated PER BUCKET ──────────────────────────────
  // Captures the initial cache once. For each bucket: if it's already in the
  // cache, skip the fetch (and clear its loading flag); if it's missing, fetch
  // only that one. This gives the "video cached → don't refetch video, but
  // blog missing → fetch only blog" behavior. A full refresh goes through
  // handleRefresh instead, which clears everything first.
  const initialCacheRef = useRef(parsedCache);
  useEffect(() => {
    if (!userEmail) return;
    const initial = initialCacheRef.current;
    // Read directly from localStorage instead of using `digitalTwinPrompt`.
    // On first mount this effect can run in the same commit as the effect
    // that loads `digitalTwinPrompt` from storage (above), before that
    // state update has applied — so the `prompt = digitalTwinPrompt`
    // default param on each fetch function would still see "". Passing the
    // freshly-read value explicitly sidesteps that race entirely.
    const initialPrompt = readStoredDigitalTwinPrompt();
    // Same idea for content_types/mode: send the saved "Tell me what to
    // create" selection (or the default 2-of-each) on the very first load,
    // instead of only sending it after a manual refresh/customize.
    const { content_types: initialContentTypes, mode: initialMode, knowledge_mode: initialKnowledgeMode } = readStoredContentTypesAndMode();

    // Opened in a language that has no suggestions yet, but another language has them:
    // translate those (stored translations) instead of generating new ones
    if (!initial && findSourceCache(getContentLanguage())) {
      showSuggestionsInRef.current(getContentLanguage()).then((ok) => {
        if (!ok) handleRefreshRef.current();
      });
      return;
    }

    if (!initial?.authorityData) {
      fetchAuthoritySuggestions(initialPrompt, initialMode, initialContentTypes, initialKnowledgeMode);
    } else {
      setIsAuthorityLoading(false);
    }

    if (!initial?.blogTriggerSuggestions?.length) {
      fetchBlogSuggestionsTrigger(initialPrompt, initialMode, initialContentTypes, initialKnowledgeMode);
    } else {
      setIsBlogTriggerLoading(false);
    }

    if (!initial?.videoSuggestions?.length) {
      fetchVideoSuggestionsTrigger(initialPrompt, initialMode, initialContentTypes, initialKnowledgeMode);
    } else {
      setIsVideoTriggerLoading(false);
    }

    if (!Array.isArray(initial?.randomCourseSuggestion) || initial.randomCourseSuggestion.length === 0) {
      fetchCourseSuggestion(initialPrompt, initialMode, initialContentTypes, initialKnowledgeMode);
    } else {
      setIsRandomCourseLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  useEffect(() => {
    const hasActiveProcessing = initiatedPosts.some(
      (p: any) => p.status === "initiated" || isInProgress(p.status)
        // social publishing still running in the scheduler (so a failure shows up on its own)
        || p.social_post_status === "scheduled" && (!p.schedule_time || Date.parse(
          /[Zz]$|[+-]\d{2}:?\d{2}$/.test(p.schedule_time) ? p.schedule_time : p.schedule_time + "Z") < Date.now() + 120000)
        || p.social_post_status === "processing"
    );
    if (!hasActiveProcessing) return;
    const interval = setInterval(() => { refetch(); }, 8000);
    return () => clearInterval(interval);
  }, [initiatedPosts, refetch]);

  // Social publishing runs in the background scheduler: when a platform fails
  // (e.g. Facebook asks to confirm identity), tell the user once with the reason.
  useEffect(() => {
    let seen: string[] = [];
    try { seen = JSON.parse(localStorage.getItem("social_errors_seen") || "[]"); } catch { /* ignore */ }
    const fresh: string[] = [];
    initiatedPosts.forEach((p: any) => {
      (p.social_errors || []).forEach((e: any) => {
        const key = `${p.id}:${e.platform}:${e.at}`;
        if (seen.includes(key) || fresh.includes(key)) return;
        fresh.push(key);
        toast.error(`${p.suggestion_title || "Your post"} was not published to ${
          e.platform ? e.platform.charAt(0).toUpperCase() + e.platform.slice(1) : "a platform"}. ${e.message || ""}`,
          { autoClose: 12000 });
      });
    });
    if (fresh.length) {
      try { localStorage.setItem("social_errors_seen", JSON.stringify([...seen, ...fresh].slice(-200))); } catch { /* ignore */ }
    }
  }, [initiatedPosts]);

  const getLatestUserDetails = async () => {
    try {
      const response = await getUserDetailsMutation({
        email: userEmail || getCookie("user_email") || "",
        filter: "",
        start_date: "",
        end_date: "",
      });

      if ("data" in response) {
        setUserDetails(response.data.data);
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  // The API now enforces generation limits itself (instead of us checking
  // latestUser.total_courses_allowed / latestUser.marketing_asset client-side)
  // and responds with { status: "failed", title, message } when a plan's
  // limit is hit or an upgrade is required. Call this with that response
  // body wherever a generate/save call can return it — it opens the upgrade
  // modal and returns true whenever the response is failed AND carries a
  // title (e.g. "Blog Generation Limit Reached", "Upgrade Required",
  // "Course Limit Reached", "Social Post Limit Reached"). It returns false
  // for a failed response with no title (e.g. "still in progress"), so the
  // caller falls back to its normal toast.error instead.
  // Generating a blog/course/post consumes credits, so this fires the
  // "creditsUpdated" event the header listens for — it refetches user
  // details immediately instead of waiting for the next page reload.
  const notifyCreditsChanged = () => {
    window.dispatchEvent(new CustomEvent("creditsUpdated"));
  };

  const handleGenerationLimitError = (data: any): boolean => {
    if (!data || data.status !== "failed") return false;
    if (!data.title) return false;

    setUpgradeModal({
      open: true,
      title: data.title,
      message:
        data.message ||
        "You've reached your plan's generation limit. Upgrade your plan to continue.",
    });
    return true;
  };

  // ── Card actions ──
  const handleGenerate = async (item: any, index: string) => {
    const latestUser = await getLatestUserDetails();
    try {
      setLoadingIndex(index);
      setPage(1);

      // Normalize platform to a single string regardless of input shape
      const platform =
        item.type === "generic" || item.type === "video"
          ? ALL_PLATFORMS_WITH_AUTHORITY
          : Array.isArray(item.platform)
            ? item.platform[0] || ""
            : item.platform || "";

      const payload = {
        type: item.type,
        title: item.title,
        hook: item.hook,
        source_reference: item.source_reference,
        platform,
        estimated_engagement: item.estimated_engagement,
        content_angle: item.content_angle,
        key_points: item.key_points,
        image_url: item.image_url,
        image_url_small: item.image_url_small,
        ...shoplineFields(item),
        ...(item.type === "video"
          ? {
            video_url: item.media_url || item.video_path || item.video_url || "",
            post_type: "clips",
          }
          : {}),
      };

      const res: any = await generatePost({
        email: userEmail,
        suggestion: payload,
      }).unwrap();

      if (res?.status === "failed" || res?.status === "error") {
        if (!handleGenerationLimitError(res)) {
          toast.error(res?.message || "Failed to generate post");
        }
        return;
      }

      toast.success("Post generation started — it'll appear in your content list shortly.");
      notifyCreditsChanged();
      setActiveTab("all");   // matches Blog/Course flow
      setPage(1);
      refetch();
      setTimeout(() => { refetch(); }, 1500);  // catch-up refetch like the others
      scrollToGeneratedContent();
    } catch (err: any) {
      console.error("Generate post failed:", err);
      if (!handleGenerationLimitError(err?.data)) {
        toast.error(err?.data?.message || "Failed to generate post");
      }
    } finally {
      setLoadingIndex(null);
    }
  };

  const handleNavigateToEdit = async (item: any, index: string) => {
    const latestUser = await getLatestUserDetails();
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;
    try {
      setLoadingIndex(index);
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const payload = {
        suggestion: {
          type: item.type, title: item.title, hook: item.hook,
          source_reference: item.source_reference,
          platform:
            item.type === "generic" || item.type === "video"
              ? ALL_PLATFORMS_WITH_AUTHORITY
              : Array.isArray(item.platform)
                ? item.platform[0]
                : item.platform,
          estimated_engagement: item.estimated_engagement,
          content_angle: item.content_angle, key_points: item.key_points,
          image_url: item.image_url, image_url_small: item.image_url_small || item.image_url,
          ...shoplineFields(item),
        },
      };
      const response = await axios.post(
        `${apiUrl}/api/prompts/api/posts/generate?user_email=${encodeURIComponent(userEmail)}`,
        payload, { headers: { "Content-Type": "application/json", accept: "application/json" } }
      );
      const responseData = response?.data || {};

      // "error" = the AI could not write the post: don't open an empty editor
      if (responseData.status === "failed" || responseData.status === "error" || !responseData.posts?.length) {
        if (!handleGenerationLimitError(responseData)) {
          toast.error(responseData?.message || "Failed to generate post");
        }
        return;
      }

      const firstPost = Array.isArray(responseData.posts) && responseData.posts.length > 0
        ? responseData.posts[0] : responseData;
      notifyCreditsChanged();
      navigate("/ocesha/publish-schedule-clip", {
        state: {
          postContent: firstPost?.content || "",
          postId: firstPost?.id || firstPost?._id || "",
          clipTitle: firstPost?.title || item.title || "",
          clipThumbnail: firstPost?.image_url || item.image_url || "",
          platform: Array.isArray(item.platform) ? item.platform[0] : item.platform || "",
          blogId: item._id || "",
          sourceType: firstPost.type || "",
          scheduleTime: firstPost?.schedule_time || "",
          // SHOPLINE product: "More Details" button in the preview
          cta_url: firstPost?.cta_url || item.product_url || "",
          cta_label: firstPost?.cta_label || (item.product_url ? "More Details" : ""),
          // product images for multi-image posts (Facebook / LinkedIn)
          product_images: item.product_images || firstPost?.shopline_product?.images || [],
          image_urls: firstPost?.image_urls || [],
        },
      });
    } catch (err: any) {
      console.error("Generate post failed:", err);
      if (!handleGenerationLimitError(err?.response?.data)) {
        toast.error(err?.response?.data?.message || "Failed to generate post");
      }
    } finally {
      setLoadingIndex(null);
      isGeneratingRef.current = false;
    }
  };

  const handlePublish = async (postId: string) => {
    try {
      setPublishingId(postId);
      await activatePost({ postId, email: userEmail }).unwrap();
      toast.success("Post published"); refetch();
    } catch { toast.error("Failed to publish"); }
    finally { setPublishingId(null); }
  };

  const handleGenerateBlog = async (item: any, index: string) => {
    const latestUser = await getLatestUserDetails();
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;
    try {
      setLoadingIndex(index);
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const triggerRes = await axios.post(
        `${apiUrl}/api/prompts/process_create_new_blog_from_suggestions_trigger`,
        { user_email: userEmail, title: item.title || "", description: item.hook || item.content_angle || "", result_count: 2,
          ...(item?.product_id ? { product_id: String(item.product_id) } : {}) },
        { headers: { "Content-Type": "application/json", accept: "application/json" } }
      );

      // NEW
      if (triggerRes.data?.status === "failed") {
        if (!handleGenerationLimitError(triggerRes.data)) {
          toast.error(
            triggerRes.data?.message ||
            "Previous blog generation is still in progress."
          );
        }

        return;
      }

      toast.success("Blog generation started — it'll appear in your Blogs list in a few minutes.");
      notifyCreditsChanged();
      setActiveTab("progress");
      setPage(1);
      refetch();
      setTimeout(() => { refetch(); }, 1500);
    } catch (err) {
      console.error("Generate Blog failed:", err);
      toast.error("Couldn't start blog generation. Please try again.");
    } finally {
      setLoadingIndex(null);
      isGeneratingRef.current = false;
    }
  };

  const handleDirectGenerateBlog = async (item: any, index: string) => {
    const latestUser = await getLatestUserDetails();
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;
    try {
      setLoadingIndex(index);
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const requestPayload = {
        user_email: userEmail,
        title: item?.title || "",
        description: item.hook || item.content_angle || "",
        result_count: 2,
        ...(item?.product_id ? { product_id: String(item.product_id) } : {}),
      };
      console.log("[AuthorityEngine] direct generate blog payload:", requestPayload);
      const triggerRes = await axios.post(
        `${apiUrl}/api/prompts/process_create_new_blog_from_suggestions_trigger`,
        requestPayload,
        { headers: { "Content-Type": "application/json", accept: "application/json" } }
      );

      // NEW
      if (triggerRes.data?.status === "failed") {
        if (!handleGenerationLimitError(triggerRes.data)) {
          toast.error(
            triggerRes.data?.message ||
            "Previous blog generation is still in progress."
          );
        }

        return;
      }


      toast.success("Blog generation started — it'll appear in your Blogs list in a few minutes.");
      notifyCreditsChanged();
      setActiveTab("progress");
      setPage(1);
      refetch();
      setTimeout(() => { refetch(); }, 1500);
      scrollToGeneratedContent();
    } catch (err) {
      console.error("Direct Generate Blog failed:", err);
      toast.error("Couldn't start blog generation. Please try again.");
    } finally {
      setLoadingIndex(null);
      isGeneratingRef.current = false;
    }
  };

  const handleCustomizedBlogGenerate = async (payload: any) => {
    const latestUser = await getLatestUserDetails();
    try {
      setLoadingIndex("custom-blog");
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const requestPayload = {
        user_email: userEmail,
        title: payload.title,
        description: payload.hook || payload.content_angle || "",
        result_count: 2,
        ...(payload?.product_id ? { product_id: String(payload.product_id) } : {}),
      };
      // Language picked in the customize dialog (this generation only)
      const langHeader = payload?.content_language ? { "X-Content-Language": payload.content_language } : {};
      console.log("[AuthorityEngine] customized blog payload:", requestPayload);
      const triggerRes = await axios.post(
        `${apiUrl}/api/prompts/process_create_new_blog_from_suggestions_trigger`,
        requestPayload,
        { headers: { "Content-Type": "application/json", accept: "application/json", ...langHeader } }
      );

      // NEW
      if (triggerRes.data?.status === "failed") {
        if (!handleGenerationLimitError(triggerRes.data)) {
          toast.error(
            triggerRes.data?.message ||
            "Previous blog generation is still in progress."
          );
        }

        return;
      }

      toast.success("Customized blog generation started successfully.");
      notifyCreditsChanged();
      setOpenCustomize(false);
      setActiveTab("progress");
      setPage(1);
      refetch();
      setTimeout(() => { refetch(); }, 1500);
    } catch (err) {
      console.error("Customized blog generation failed:", err);
      toast.error("Failed to generate customized blog.");
    } finally {
      setLoadingIndex(null);
    }
  };

  const handleSaveCourseAndPush = async (item: any, index: string) => {
    const latestUser = await getLatestUserDetails();
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;
    setIsCourseSavingPushing(true);
    try {
      setLoadingIndex(index);
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";

      // Use the item passed in (clicked card) directly; fall back to localStorage
      let courseData: any = item || {};
      try {
        const stored = localStorage.getItem("course_suggestion_selected");
        if (stored && !courseData.parent_course_id) {
          courseData = { ...JSON.parse(stored), ...courseData };
        }
      } catch (e) {
        console.warn("Could not read course_suggestion_selected:", e);
      }

      const numericPrice =
        typeof coursePrice === "string"
          ? coursePrice === "" ? 0 : Number(coursePrice)
          : Number(coursePrice) || 0;

      // Customization data coming from CustomizeCourseDialog's "Next: Generate
      // Course" button (courseType, chapters, quizzes, presenter, extras, etc.)
      const customization = courseData.customization || null;

      // The real knowledge mode for THIS course lives on
      // customization.generationMode (set by CustomizeCourseDialog when the
      // user picks Faithful/Balanced/Expanded for this specific course).
      // The old code sent a separate top-level `knowledge_mode` derived from
      // localStorage, which could disagree with generationMode entirely
      // (confirmed: generationMode="faithful" but the old top-level field
      // came out "balanced") -- causing the backend to skip the faithful-mode
      // failure check, and the frontend poll to skip the matching message.
      const validKnowledgeModesList = ["faithful", "balanced", "expanded"];
      const modeFromCustomization = customization?.generationMode;
      const validKnowledgeModes = validKnowledgeModesList.includes(modeFromCustomization || "")
        ? modeFromCustomization
        : (readStoredContentTypesAndMode().knowledge_mode || "balanced");

      const savePayload = {
        parent_course_id: courseData.parent_course_id || localStorage.getItem("course_id") || "",
        title: customization?.title || courseData.title || "",
        name: courseData.name || "",
        description: courseData.description || courseData.hook || "",
        difficulty: customization?.difficulty || courseDifficulty,
        target_audience: courseData.target_audience || "",
        suggested_price: numericPrice,
        user_email: userEmail,
        duration: customization?.duration || courseData.duration || "",
        learning_outcomes: courseData.learning_outcomes || courseData.key_points || [],
        curriculum: courseData.curriculum || [],
        // Pass the full customization block through to the backend as-is.
        // Adjust the key/shape here to match whatever your save-course API expects.
        customization: customization || undefined,
        knowledge_mode: validKnowledgeModes,
      };



      console.log("[AuthorityEngine] save-course payload:", savePayload);

      const triggerRes = await axios.post(
        `${apiUrl}/api/prompts/save-course`,
        savePayload,
        { headers: {
          "Content-Type": "application/json", accept: "application/json",
          // Language picked in the customize dialog (this course only)
          ...(customization?.content_language ? { "X-Content-Language": customization.content_language } : {}),
        } }
      );

      // NEW
      if (triggerRes.data?.status === "failed") {
        if (!handleGenerationLimitError(triggerRes.data)) {
          toast.error(
            triggerRes.data?.message ||
            "Previous course generation is still in progress."
          );
        }

        return;
      }

      
      setActiveTab("progress");
      setPage(1);
      await refetch();
      setTimeout(() => { refetch(); }, 1500);  // catch-up refetch like the others
      scrollToGeneratedContent();

      // axios.post(
      //   `${apiUrl}/api/prompts/push-courses`,
      //   {},
      //   {
      //     params: {
      //       parent_course_id: courseData.parent_course_id || localStorage.getItem("course_id") || "",
      //       user_email: userEmail,
      //     },
      //     headers: { accept: "application/json" },
      //   }
      // );
 
      toast.success("Course saved — it'll appear in your courses list shortly.");
      notifyCreditsChanged();

      // Fire-and-forget: watch this course's generation in the background and
      // clean up / notify the user if it fails (e.g. faithful mode found no
      // matching source content). Not awaited — don't block the UI on this.
        // const savedCourseId = triggerRes.data?.course_id;
        // if (savedCourseId) {
        //   pollCourseGenerationForFailure(savedCourseId, validKnowledgeModes as string);
        // }
      const savedCourseId = triggerRes.data?.course_id;
      if (savedCourseId) {
        startTracking(savedCourseId, {
          onComplete: () => refetch(),
          onFail: async (errorMsg) => {
            if (errorMsg === "no data found" && validKnowledgeModes === "faithful") {
              toast.error("No relevant data found in your uploaded content. Please choose a different mode rather than Faithful.");
            } else {
              toast.error("Course generation failed. Please try again.");
            }
            try {
              const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
              await axios.post(`${apiUrl}/api/prompts/delete_post_and_course`, null, { params: { course_id: savedCourseId } });
            } catch (delErr) {
              console.error("Failed to delete incomplete course:", delErr);
            }
            refetch();
            notifyCreditsChanged();
          },
        });
      }
      // setActiveTab("progress");
      // setPage(1);
      // refetch();
      // setTimeout(() => { refetch(); }, 1500);
      //scrollToGeneratedContent();
    } catch (err) {
      console.error("Save/push course failed:", err);
      toast.error("Couldn't save course. Please try again.");
    } finally {
      setLoadingIndex(null);
      setIsCourseSavingPushing(false);
      isGeneratingRef.current = false;
    }
  };

  const handleGenerateCourse = async (item: any, index: string) => {
    const latestUser = await getLatestUserDetails();
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;
    try {
      setLoadingIndex(index);
      scrollToGeneratedContent();
      navigate("/codegenie/Course_Suggestions", {
        state: {
          title: item.title || "",
          description: item.hook || item.content_angle || "",
          course_id: item.course_id || "",
          category: item.category || "",
          target_audience: item.target_audience || "",
          difficulty_level: item.difficulty_level || "",
          estimated_duration: item.estimated_duration || "",
          learning_outcomes: item.key_points || item.learning_outcomes || [],
          suggested_modules: item.suggested_modules || [],
          prerequisites: item.prerequisites || "",
          tags: item.tags || [],
          image_url: item.image_url || "",
          fromAuthorityEngine: true,
        },
      });
    } catch (err) {
      console.error("Navigate to Course_Suggestions failed:", err);
      toast.error("Couldn't open course suggestions. Please try again.");
    } finally {
      setLoadingIndex(null);
      isGeneratingRef.current = false;
    }
  };

  const handleUpdatePost = async () => {
    try {
      await updatePost({
        postId: editingPost.id, content: editedContent,
        platform: editingPost.platform, scheduleTime: editingPost.scheduleTime,
        imageUrl: editingPost.image_url, url: editingPost.url || "",
        video_url: editingPost.video_url || "",
      }).unwrap();
      toast.success("Post updated");
      setOpenEditor(false);
    } catch { toast.error("Update failed"); }
  };

  const handleEditBlogPost = async (blogId: string, loadingId?: string) => {
    if (!blogId) {
      toast.error("No blog id found for this post");
      return;
    }
    try {
      if (loadingId) setEditButtonLoadingId(loadingId);
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const response = await axios.get(`${apiUrl}/api/prompts/getBlogDet`, {
        params: { blog_id: blogId, campId: null },
        headers: { accept: "application/json" },
      });

      if (response.data.status === "success" && response.data.data?.length > 0) {
        const blog = response.data.data[0];
        // Navigate to the edit-blog screen, passing the full blog as state
        // (the EditBlog page reads location.state, exactly like Blogs_Section does)
        navigate("/ocesha-edit-blog", { state: blog });
      } else {
        toast.error("Could not load blog details");
      }
    } catch (err) {
      console.error("Failed to load blog for editing:", err);
      toast.error("Failed to load blog details");
    } finally {
      if (loadingId) setEditButtonLoadingId(null);
    }
  };

  // ── Memos ──
  const recentActivities = useMemo(() => {
    const typeConfig: Record<string, { title: string; icon: any; bg: string }> = {
      blog: { title: "Blog post generated", icon: <ArticleOutlinedIcon sx={{ fontSize: 18, color: "#C4B5FD" }} />, bg: "linear-gradient(135deg,#6D28D9,#7C3AED)" },
      post: { title: "Social post published", icon: <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: "#DDD6FE" }} />, bg: "linear-gradient(135deg,#5B21B6,#7C3AED)" },
      course: { title: "Course created", icon: <SchoolOutlinedIcon sx={{ fontSize: 18, color: "#BBF7D0" }} />, bg: "linear-gradient(135deg,#047857,#10B981)" },
      video: { title: "Video clip generated", icon: <PlayCircleOutlinedIcon sx={{ fontSize: 18, color: "#FED7AA" }} />, bg: "linear-gradient(135deg,#C2410C,#EA580C)" },
      image: { title: "Image generated", icon: <DescriptionOutlinedIcon sx={{ fontSize: 18, color: "#DDD6FE" }} />, bg: "linear-gradient(135deg,#6D28D9,#7C3AED)" },
    };
    const mapItem = (item: any) => {
      const cfg = typeConfig[item.type] || typeConfig.post;
      let subtitle = item.title || item.subtitle || "";
      subtitle = subtitle.replace(/\n+/g, " ").trim();
      if (subtitle.length > 70) subtitle = subtitle.substring(0, 70) + "...";
      if (!subtitle) subtitle = item.type === "video" ? "New video clip" : "—";
      const createdAt = item.created_at || objectIdToDate(item._id);
      return { ...cfg, subtitle, time: timeAgo(createdAt), created_at: createdAt, _id: item._id, type: item.type };
    };
    const all = [
      ...(recentActivityData.blogs || []),
      ...(recentActivityData.courses || []),
      ...(recentActivityData.posts || []),
      ...(recentActivityData.videos || []),
      ...(recentActivityData.images || []),
    ].map(mapItem);
    return all
      .sort((a, b) => parseTimestamp(b.created_at) - parseTimestamp(a.created_at))
      .slice(0, 5);
  }, [recentActivityData]);

  const authorityScore = () => authoritySignals.authority_score || 0;
  const getPlatformCounts = () => [
    { icon: <LinkedInIcon sx={{ fontSize: 14, color: "#4BA5EB" }} />, label: "LinkedIn Posts", val: authoritySignals.linkedin || 0 },
    { icon: <TwitterIcon sx={{ fontSize: 14, color: "#1DA1F2" }} />, label: "X/Twitter", val: authoritySignals.twitter || 0 },
    { icon: <YouTubeIcon sx={{ fontSize: 14, color: "#FF0000" }} />, label: "Youtube", val: authoritySignals.youtube || 0 },
    { icon: <FacebookIcon sx={{ fontSize: 14, color: "#1877F2" }} />, label: "Facebook Posts", val: authoritySignals.facebook || 0 },
  ];

  const StatusBadge = ({ status }: any) => {

    // Don't render anything for draft state — keep the column clean.
    if (status === "draft" || !status) return null;

    const config: any = {
      ready: { label: "Ready to Publish", bg: "rgba(34,197,94,0.15)", color: "#4ADE80" },
      pending: { label: "Scheduled", bg: "rgba(234,179,8,0.15)", color: "#FACC15" },
      in_progress: { label: "In Process", bg: "rgba(59,130,246,0.15)", color: "#60A5FA", animated: true },
      inprogress: { label: "In Process", bg: "rgba(59,130,246,0.15)", color: "#60A5FA", animated: true },
      initiated: { label: "Initiated", bg: "rgba(139,92,246,0.15)", color: "#A78BFA" },
      draft: { label: "", bg: "rgba(255,255,255,0.08)", color: "#fff" },
      scheduled: { label: "Scheduled", bg: "rgba(234,179,8,0.15)", color: "#FACC15" },
      completed: { label: "Ready to Publish", bg: "rgba(34,197,94,0.15)",color: "#4ADE80"},
      generated: { label: "Ready to Publish", bg: "rgba(34,197,94,0.15)",color: "#4ADE80"},
      unpublished: { label: "Unpublished", bg: "rgba(239,68,68,0.15)",color: "#F87171"},
      published: { label: "Published", bg: "rgba(168,85,247,0.15)",color: "#C084FC"},
      failed: { label: "Failed", bg: "rgba(239,68,68,0.15)",color: "#F87171"},
      cancelled: { label: "Ready to Publish", bg: "rgba(34,197,94,0.15)",color: "#4ADE80"},
      canceled: { label: "Ready to Publish", bg: "rgba(34,197,94,0.15)",color: "#4ADE80"},
    };
    const item = config[status] || config.draft;
    return (
      <Box sx={{
        display: "inline-flex", alignItems: "center", gap: 0.6,
        px: 1.5, py: 0.5, borderRadius: "999px",
        background: item.bg, color: item.color,
        fontSize: "12px", fontWeight: 600,
      }}>
        {item.animated && (
          <CircularProgress size={10} thickness={6} sx={{ color: item.color }} />
        )}
        {item.label}
      </Box>
    );
  };



  const filteredPosts = initiatedPosts.filter((post: any) => {
    if (activeTab === "all") return true;
    if (activeTab === "ready") return post.status === "ready" || post.status === "completed";
    if (activeTab === "scheduled") return post.status === "scheduled" || post.status === "pending";
    if (activeTab === "progress")
      return isInProgress(post.status) || post.status === "initiated";
    return true;
  });

  const isSuggestionsRefreshing =
  isAuthorityLoading ||
  isBlogTriggerLoading ||
  isVideoTriggerLoading ||
  isRandomCourseLoading;

  // Blogs/social/generic are the "fast" buckets — once all three are done,
  // only the course bucket is left generating (it's the slow one).
  const areBlogsAndSocialReady =
    !isAuthorityLoading && !isBlogTriggerLoading && !isVideoTriggerLoading;

  // Tick once a second while anything is still loading, so getStatusMessage()
  // below re-evaluates elapsed time even though no real state changed.
  useEffect(() => {
    if (!isSuggestionsRefreshing) return;
    const interval = setInterval(() => setStatusTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [isSuggestionsRefreshing]);

  // Stamp the moment blogs/social/generic finish, once per refresh cycle.
  useEffect(() => {
    if (areBlogsAndSocialReady && refreshStartRef.current && !blogsReadyRef.current) {
      blogsReadyRef.current = Date.now();
    }
  }, [areBlogsAndSocialReady]);




  // Drives the status line under "Recommended for You" while generating.
  // Uses actual bucket-completion state to decide WHICH phase we're in
  // (pre-blogs vs. course-only), and elapsed time only to pick the message
  // within that phase — per the client's guidance to prefer real state over
  // a purely decorative animation.
  const getStatusMessage = (): string | null => {
    if (!isSuggestionsRefreshing || !refreshStartRef.current) return null;

    if (!areBlogsAndSocialReady) {
      const secs = (Date.now() - refreshStartRef.current) / 1000;
      if (secs < 3) return "Digital Twin Analyzing your request...";
      if (secs < 6) return "Reviewing your knowledge and content...";
      return "Finding the strongest content opportunities...";
    }

    // Blogs/social are ready — only course generation is left, if anything.
    if (!isRandomCourseLoading) return null;

    const courseSecs = blogsReadyRef.current
      ? (Date.now() - blogsReadyRef.current) / 1000
      : 0;
    if (courseSecs < 5) return "Your initial recommendations are ready. Exploring course opportunities...";
    if (courseSecs < 10) return "Matching course ideas to your expertise...";
    if (courseSecs < 15) return "Developing course recommendations...";
    if (courseSecs < 20) return "Refining course topics and positioning...";
    if (courseSecs < 25) return "Selecting the strongest course recommendations...";
    return "Finishing your course recommendations...";
  };

  void statusTick; // read so this render (triggered by the 1s ticker) recomputes the message below
  const currentStatusMessage = getStatusMessage();

  // Refresh button label: idle → "↻ Refresh"; blogs/social done but course
  // still cooking → "✦ Creating More Recommendations..."; otherwise the
  // generic "Refreshing...".
  const refreshButtonLabel = !isSuggestionsRefreshing
    ? "Refresh"
    : (areBlogsAndSocialReady && isRandomCourseLoading)
      ? "Creating More Recommendations..."
      : "Refreshing...";

  const getDisplayContent = (content = "") =>
    content.trim().replace(/^['"]+|['"]+$/g, "");

  // ═══════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ background: "#000000", color: "#fff", fontFamily: "'Inter', -apple-system, sans-serif" }}>

      {/* Welcome + Refresh */}
      <Stack
        direction={{ xs: "column", lg: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", lg: "center" }}
        spacing={3}
        mb={3}
      >
        {/* LEFT CONTENT */}
        <Box sx={{ flex: 1, width: "100%" }}>
          <Typography
            sx={{
              fontSize: { xs: 28, md: 36 },
              fontWeight: 800,
              color: "#fff",
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            {/* whole phrase in one text node so it is translated as one (data-i18n: always
                translated); the name is never translated */}
            <span data-i18n="">{userDetails?.logged_in == false ? "Welcome" : "Welcome back"}</span>{" "}
            <span className="notranslate">{userDetails?.first_name}</span>!
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.65)",
              fontSize: 15,
              lineHeight: 1.7,
            }}
          >
            Here are ideas and assets you can turn into content today based on your
            expertise in AI Strategy, Emerging Technology, AI Discovery
          </Typography>
        </Box>
      </Stack>

      {/* SIDE-BY-SIDE LAYOUT */}
      <Grid container spacing={1} alignItems="flex-start">

        {/* Col 1: Recommendations */}
        <Grid item xs={12} lg={9.5}>
          <Box sx={{
            background: "linear-gradient(180deg, rgba(17,24,39,0.96) 0%, rgba(8,12,20,0.96) 100%)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: "14px", p: 2, pt: 1.5,
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>
                  Recommended for You
                </Typography>
                <Typography
                  sx={{
                    color: currentStatusMessage ? "#B695F8" : "rgba(255,255,255,0.55)",
                    fontSize: 13,
                    mt: 0.25,
                    fontWeight: currentStatusMessage ? 600 : 400,
                    transition: "color 0.2s ease",
                  }}
                >
                  {currentStatusMessage || "AI-powered recommendations based on your expertise"}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                {/* Language of the suggestions (default: same as the site language).
                    Changing it regenerates the suggestions in that language. */}
                <ContentLanguageSelect mode="global" sx={{ height: 40, borderRadius: "14px" }} />
                <Tooltip title="Refresh recommendations">
                  <span>
                    <Button
                      startIcon={
                        areBlogsAndSocialReady && isRandomCourseLoading ? (
                          <AutoAwesomeIcon
                            fontSize="small"
                            sx={{
                              animation: "pulse 1.2s ease-in-out infinite",
                              "@keyframes pulse": {
                                "0%, 100%": { opacity: 1, transform: "scale(1)" },
                                "50%": { opacity: 0.55, transform: "scale(0.85)" },
                              },
                            }}
                          />
                        ) : (
                          <RefreshIcon
                            fontSize="small"
                            sx={{
                              animation: isSuggestionsRefreshing ? "spin 1s linear infinite" : "none",
                              "@keyframes spin": {
                                "0%": { transform: "rotate(0deg)" },
                                "100%": { transform: "rotate(360deg)" },
                              },
                            }}
                          />
                        )
                      }
                      onClick={() => handleRefresh()}
                      disabled={isSuggestionsRefreshing}
                      sx={{
                        height: 40,
                        px: 3,
                        borderRadius: "14px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: "#fff",
                        background:
                          "linear-gradient(135deg,#7C3AED 0%, #9333EA 100%)",

                        "&:hover": {
                          opacity: 0.92,
                          background:
                            "linear-gradient(135deg,#7C3AED 0%, #9333EA 100%)",
                        },

                        "&.Mui-disabled": {
                          background:
                            "linear-gradient(135deg,#7C3AED 0%, #9333EA 100%)",
                          opacity: 0.6,
                          color: "#fff",
                        },
                      }}
                    >
                      {refreshButtonLabel}
                    </Button>
                  </span>
                </Tooltip>

                <Typography
                  onClick={() => setShowAllRecommendations(!showAllRecommendations)}
                  sx={{
                    color: "#8B5CF6", fontSize: 13,
                    cursor: "pointer",
                    "&:hover": { opacity: 0.8 },
                  }}
                >
                  {showAllRecommendations ? "Show Less" : "View all recommendations →"}
                </Typography>
              </Stack>
            </Stack>

            {/* ═══════════════════════════════════════════════════════════════
                PER-BUCKET RENDERING — each section flips from skeleton → cards
                independently, so the user sees results as each API finishes
                rather than waiting for all four.
                ═══════════════════════════════════════════════════════════════ */}
            <Grid container spacing={1}>
              {showAllRecommendations ? (
                <>

                  {/* Course bucket — bucket takes longest, so it gets the
                      dedicated "building" skeleton with live status copy
                      once blogs/social are already done. */}
                  {isRandomCourseLoading
                    ? (
                      <Grid item xs={12} sm={6} md={4} key="course-skel-0">
                        <CourseRecommendationSkeleton
                          label={areBlogsAndSocialReady && currentStatusMessage
                            ? currentStatusMessage
                            : "Building your course recommendations..."}
                        />
                      </Grid>
                    )
                    : randomCourseSuggestion.map((item: any, i: number) =>
                      renderRecommendationCard(item, 300 + i)
                    )}

                  {/* Blog bucket */}
                  {isBlogTriggerLoading
                    ? (
                      <Grid item xs={12} sm={6} md={4} key="blog-skel-0">
                        <RecommendationCardSkeleton label="Finding blog recommendations..." />
                      </Grid>
                    )
                    : blogTriggerSuggestions.map((item: any, i: number) =>
                      renderRecommendationCard(item, 100 + i)
                    )}

                  {/* Video bucket */}
                  {isVideoTriggerLoading
                    ? (
                      <Grid item xs={12} sm={6} md={4} key="video-skel-0">
                        <RecommendationCardSkeleton label="Finding video recommendations..." />
                      </Grid>
                    )
                    : videoSuggestions.map((item: any, i: number) =>
                      renderRecommendationCard(item, 200 + i)
                    )}

                  {/* Generic / authority bucket */}
                  {isAuthorityLoading
                    ? (
                      <Grid item xs={12} sm={6} md={4} key="generic-skel-0">
                        <RecommendationCardSkeleton label="Building your recommendations..." />
                      </Grid>
                    )
                    : normalizedGenericSuggestions.map((item: any, i: number) =>
                      renderRecommendationCard(item, 400 + i)
                    )}
                </>
              ) : (
                <>
                  {/* Collapsed view — one card per type */}

                  {(() => {
                    if (isRandomCourseLoading) {
                      return (
                        <Grid item xs={12} sm={6} md={4} key="slot-course-skel">
                          <CourseRecommendationSkeleton
                            label={areBlogsAndSocialReady && currentStatusMessage
                              ? currentStatusMessage
                              : "Building your course recommendations..."}
                          />
                        </Grid>
                      );
                    }
                    const courseCard = randomCourseSuggestion?.[0];
                    return courseCard ? renderRecommendationCard(courseCard, 2) : null;
                  })()}

                  {(() => {
                    const blogCard = blogTriggerSuggestions?.[0];
                    if (isBlogTriggerLoading) {
                      return (
                        <Grid item xs={12} sm={6} md={4} key="slot-blog-skel">
                          <RecommendationCardSkeleton label="Finding blog recommendations..." />
                        </Grid>
                      );
                    }
                    return blogCard ? renderRecommendationCard(blogCard, 0) : null;
                  })()}

                  {(() => {
                    const videoCard = videoSuggestions?.[0];
                    if (isVideoTriggerLoading) {
                      return (
                        <Grid item xs={12} sm={6} md={4} key="slot-video-skel">
                          <RecommendationCardSkeleton label="Finding video recommendations..." />
                        </Grid>
                      );
                    }
                    return videoCard ? renderRecommendationCard(videoCard, 1) : null;
                  })()}


                  {(() => {
                    const genericCard = normalizedGenericSuggestions?.[0];
                    if (isAuthorityLoading) {
                      return (
                        <Grid item xs={12} sm={6} md={4} key="slot-generic-skel">
                          <RecommendationCardSkeleton label="Building your recommendations..." />
                        </Grid>
                      );
                    }
                    return genericCard ? renderRecommendationCard(genericCard, 3) : null;
                  })()}
                </>
              )}
            </Grid>
          </Box>
        </Grid>

        {/* Col 2: My Digital Twin + Authority Signals + Recent Activity */}
        <Grid item xs={12} lg={2.5}>
          <Box sx={{ mb: 1 }}>
            <MyDigitalTwinPopup
              userName={userDetails?.first_name || "There"}
              userImage={userDetails?.user_image || UserImg}
              handleRefresh={handleRefresh}
            />
          </Box>
          <DarkCard sx={{ p: 2 }}>
            <Typography fontWeight={600} fontSize="15px" mb={2}>
              Authority Signals
            </Typography>
            <Typography color="#D0D5DD" fontSize="12px" mb={0.25}>
              Authority Score
            </Typography>
            <Typography fontSize="44px" fontWeight={700} lineHeight={1.1} mb={2}>
              {authorityScore()}
            </Typography>

            <Stack spacing={1.2} mb={2}>
              {statsLoading ? (
                <Typography fontSize="12px" color="#aaa">Loading stats...</Typography>
              ) : (
                getPlatformCounts().map((s, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {s.icon}
                      <Typography color="#D0D5DD" fontSize="13px">{s.label}</Typography>
                    </Stack>
                    <Typography fontWeight={600} fontSize="13px">{s.val}</Typography>
                  </Stack>
                ))
              )}
            </Stack>
          </DarkCard>

          <Box sx={{
            mt: 1.5,
            background: "linear-gradient(180deg, rgba(17,24,39,0.96) 0%, rgba(8,12,20,0.96) 100%)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: "14px", p: 2,
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
                Recent Activity
              </Typography>
            </Stack>

            <Stack spacing={1.5}>
              {recentActivities.map((item, idx) => (
                <Stack key={idx} direction="row" spacing={1.2} alignItems="flex-start">
                  <Box sx={{
                    width: 38, height: 38, borderRadius: "10px", background: item.bg,
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    {item.icon}
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", "&:hover": { color: "#60A5FA" } }}
                        onClick={() => {
                          switch (item.type) {
                            case "blog":
                              localStorage.setItem("activeItem", "blogs");
                              navigate(0);
                              break;
                            case "post":
                              localStorage.setItem("activeItem", "socailpost");
                              navigate(0);
                              break;
                            case "course":
                              localStorage.setItem("activeItem", "createcourse");
                              navigate(0);
                              break;
                            case "video":
                              localStorage.setItem("activeItem", "episodes");
                              navigate(0);
                              break;
                            case "image":
                              localStorage.setItem("activeItem", "images");
                              navigate(0);
                              break;
                            default: break;
                          }
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11, whiteSpace: "nowrap" }}>
                        {item.time}
                      </Typography>
                    </Stack>
                    <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12.5, mt: 0.3, lineHeight: 1.45 }}>
                      {item.subtitle}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Grid>
      </Grid>


      {/* GENERATED CONTENT */}
      <Box
        ref={generatedContentRef}
        mt={3}
        sx={{
          background: "radial-gradient(circle at top left, rgba(0,102,255,0.12), transparent 40%)",
          borderRadius: "20px",
          border: "1px solid rgba(255,255,255,0.06)",
          p: 3,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>
              Generated Content
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 13, mt: 0.25 }}>
              Review, edit, and publish your content
            </Typography>
          </Box>

          <FormControl size="small">
            <Select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              sx={{
                minWidth: 160, borderRadius: "10px",
                background: "rgba(15,23,42,0.6)", color: "#fff", fontSize: 13,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.1)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
                "& .MuiSelect-icon": { color: "rgba(255,255,255,0.5)" },
              }}
            >
              <MenuItem value="recent">Most Recent</MenuItem>
              <MenuItem value="oldest">Oldest</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            mb: 2.5, minHeight: 42,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-flexContainer": { gap: 1 },
          }}
        >
          {tabs.map((tab) => {
            const count =
              tab.key === "all" ? total :
                tab.key === "ready" ? initiatedPosts.filter((p: any) => p.status === "ready" || p.status === "completed").length :
                  tab.key === "scheduled" ? initiatedPosts.filter((p: any) => p.status === "scheduled" || p.status === "pending").length :
                    tab.key === "progress" ? initiatedPosts.filter((p: any) => isInProgress(p.status) || p.status === "initiated").length :
                      0;

            const selected = activeTab === tab.key;

            return (
              <Tab
                key={tab.key}
                value={tab.key}
                disableRipple
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>{tab.label}</span>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        minWidth: 22, height: 20, px: 0.75,
                        borderRadius: "999px", fontSize: 11, fontWeight: 700,
                        background: selected ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)",
                        color: selected ? "#fff" : "rgba(255,255,255,0.6)",
                      }}
                    >
                      {count}
                    </Box>
                  </Box>
                }
                sx={{
                  textTransform: "none", borderRadius: "10px", minHeight: 38,
                  px: 2, py: 0.5, fontSize: 13.5, fontWeight: 600,
                  color: "rgba(255,255,255,0.6)", background: "transparent",
                  "&.Mui-selected": { background: "#5B4DFF", color: "#fff" },
                  "&:hover": { background: selected ? "#5B4DFF" : "rgba(255,255,255,0.04)" },
                }}
              />
            );
          })}
        </Tabs>

        {initiatedLoading || (initiatedFetching && paginationLoadingKey !== null) ? (
          <Stack spacing={2}>
            {Array.from({ length: 3 }).map((_, i) => <PostSkeleton key={i} />)}
          </Stack>
        ) : filteredPosts.length === 0 ? (
          <Box sx={{
            textAlign: "center", py: 6, px: 2,
            background: "rgba(21,21,29,0.4)", borderRadius: "14px",
            border: "1px solid rgba(255,255,255,0.06)",
          }}>
            <Box sx={{
              width: 60, height: 60, mx: "auto", mb: 2, borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 28, color: "rgba(255,255,255,0.5)" }} />
            </Box>
            <Typography fontSize="16px" fontWeight={500} mb={1}>No content yet</Typography>
            <Typography fontSize="13px" color="rgba(255,255,255,0.5)">
              Generated posts will appear here once you create them
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: GENERATED_CONTENT_GRID_TEMPLATE,
                alignItems: "center",
                gap: 2,
                px: 1,
                py: 1.25,
                mb: 1,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.55)",
                fontSize: 12,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {/* Thumbnail placeholder — same grid column as the row image below */}
              <Box />

              {/* Type icon — matches the TypeChip column */}
              <Box sx={{ textAlign: "left" }}>Content Type</Box>

              {/* Platform badges */}
              <Box sx={{ textAlign: "left" }}>Platform</Box>

              {/* Title */}
              <Box sx={{ minWidth: 0, textAlign: "left" }}>Title</Box>

              {/* Status badge */}
              <Box sx={{ textAlign: "left" }}>Status</Box>

              {/* Created / scheduled time */}
              <Box sx={{ textAlign: "right" }}>Created</Box>

              {/* Action buttons */}
              <Box sx={{ textAlign: "right" }}>Actions</Box>
            </Box>
            {filteredPosts.map((post: any, index: number) => {
              const np = getPlatform(post.platform);

              const typeLabel =
                post.type === "blog" ? "Blog Article" :
                  post.type === "video" ? "VidPlatformBadgeseo Clip" :
                    post.type === "course" ? "Course" :
                      post.type === "post" && post.post_type === "video" ? "Clip" :
                        np === "linkedin" ? "LinkedIn Post" :
                          np === "twitter" ? "Twitter Post" :
                            np === "facebook" ? "Facebook Post" :
                              np === "youtube" ? "YouTube Video" :
                                np === "authority" ? "Profile Page" :
                                  "Social Post";

              const rightTimeLabel =
                post.status === "pending"
                  ? (post.schedule_time ? formatScheduleShort(post.schedule_time) : "Scheduled")
                  : timeAgo(post.created_at || objectIdToDate(post._id || post.id));

              return (
                <Box
                  key={post.id || index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: GENERATED_CONTENT_GRID_TEMPLATE,
                    alignItems: "center", gap: 2,
                    px: 1, py: 1.5,
                    borderBottom: index === filteredPosts.length - 1
                      ? "none" : "1px solid rgba(255,255,255,0.06)",
                    transition: "background 0.15s",
                    "&:hover": { background: "rgba(255,255,255,0.02)" },
                  }}
                >
                  <Box sx={{ position: "relative", width: 64, height: 48, flexShrink: 0 }}>
                    <Box
                      component="img"
                      src={post.image_url || post.image || "https://picsum.photos/100/70"}
                      onError={(e: any) => { e.currentTarget.src = "https://picsum.photos/100/70"; }}
                      sx={{
                        width: 64, height: 48, borderRadius: "8px",
                        objectFit: "cover", display: "block",
                      }}
                    />

                    {isInProgress(post.status) && (
                      <Box sx={{
                        position: "absolute", inset: 0, borderRadius: "8px",
                        background: "rgba(0,0,0,0.55)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        backdropFilter: "blur(1px)",
                      }}>
                        <CircularProgress size={20} thickness={5} sx={{ color: "#60A5FA" }} />
                      </Box>
                    )}
                  </Box>

                  <Box>
                    {post.type ? (
                      <TypeChip platform={post.type} type="label" postType={post.post_type} />
                    ) : (
                      <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>—</Typography>
                    )}
                  </Box>

                  <Box>
                    {np ? (
                      <PlatformBadges platforms={post.platform} />
                    ) : (
                      <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>—</Typography>
                    )}
                  </Box>


                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: 14, fontWeight: 500, color: "#fff",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {post.suggestion_title || post.title || getDisplayContent(post.content).substring(0, 70) || typeLabel}
                    </Typography>
                    {((post.has_draft && post.draft) || post.source_has_draft) && (
                      <Tooltip title="You have saved changes that are not live yet. Open Edit to review or publish them.">
                        <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 600, color: "#7fc2f5", cursor: "default" }}>
                          Draft changes saved
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>

                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.25}>
                      <StatusBadge status={post.status} />
                      {Array.isArray(post.social_errors) && post.social_errors.length > 0 && (
                        <Tooltip
                          title={post.social_errors.map((e: any) =>
                            `${(e.platform || "").charAt(0).toUpperCase() + (e.platform || "").slice(1)}: ${e.message || "Publishing failed"}`).join("\n")}
                          componentsProps={{ tooltip: { sx: { whiteSpace: "pre-line", fontSize: 12, maxWidth: 340 } } }}
                        >
                          <Box component="span" sx={{
                            ml: 0.5, px: 0.75, py: 0.2, borderRadius: "6px", fontSize: 11, fontWeight: 600, cursor: "help",
                            color: "#F87171", background: "rgba(248,113,113,0.10)", border: "1px solid rgba(248,113,113,0.35)",
                            whiteSpace: "nowrap",
                          }}>
                            ⚠ Failed: {post.social_errors.map((e: any) => (e.platform || "").charAt(0).toUpperCase() + (e.platform || "").slice(1)).join(", ")}
                          </Box>
                        </Tooltip>
                      )}
                      {(post.status === "scheduled" || post.status === "pending") && (
                        <Tooltip title="Cancel scheduled post">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setPostToCancel(post);
                              setOpenCancelConfirm(true);
                            }}
                            sx={{
                              p: 0.4,
                              color: "rgba(248,113,113,0.7)",
                              "&:hover": { color: "#F87171", background: "rgba(248,113,113,0.08)" },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    {(post.status === "scheduled" || post.status === "pending") && post.schedule_time && (
                      <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.5)", mt: 0.4 }}>
                        {formatScheduleShort(post.schedule_time)}
                      </Typography>
                    )}
                  </Box>

                  <Typography sx={{
                    fontSize: 12.5, color: "rgba(255,255,255,0.55)",
                    textAlign: "right",
                  }}>
                    {rightTimeLabel}
                  </Typography>

                  <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" rowGap={0.75}>
                    {!isInProgress(post.status) && post.status !== "initiated" && (post.platform === "course" || post.type === "course" || post.platform === "blog" || post.type === "blog") && (
                      <Button
                        size="small"
                        startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 15 }} />}
                        onClick={() => {
                          if (post.platform === "course" || post.type === "course") {
                            const title = post.suggestion_title || post.title || "";
                            const courseId = post.content_id || post.id || post._id || "";
                            navigate(`/course/${createCourseSlug(title, courseId)}`);
                          } else {
                            // Blog: same preview route/pattern as Blogs_Section's handleViewClick
                            const blogId = post.content_id || post.id || post._id || "";
                            window.location.href = `/PreviewBlogs1/${blogId}?campId=${selectedCampaign}`;
                          }
                        }}
                        sx={listActionBtnStyle}
                      >
                        View
                      </Button>
                    )}
                    {!isInProgress(post.status) && post.status !== "initiated" && (
                      <Button
                        size="small"
                        startIcon={
                          editButtonLoadingId === (post.id || post._id || post.content_id)
                            ? <CircularProgress size={13} sx={{ color: "inherit" }} />
                            : <EditOutlinedIcon sx={{ fontSize: 15 }} />
                        }
                        disabled={editButtonLoadingId === (post.id || post._id || post.content_id)}
                        onClick={() => {
                          const rowId = post.id || post._id || post.content_id;
                          if (post.platform === "course" || post.type === "course") {
                            navigate("/codegenie/edit-course", {
                              state: { courseId: post.content_id},
                            });
                          }
                          else if (post.platform === "blog" || post.type === "blog") {
                             handleEditBlogPost(post.content_id, rowId);
                          } 
                          else {
                            // social post / video clip / episode / image -> publish & schedule page
                            goToPublishSchedule(post);
                          }
                         }
                        }
                        sx={listActionBtnStyle}
                      >
                        Edit
                      </Button>
                    )}
                    {isPublishedAnywhere(post) && (
                      <Button
                        size="small"
                        startIcon={<VisibilityOffOutlinedIcon sx={{ fontSize: 15 }} />}
                        onClick={() => {
                          setPostToUnpublish(post);
                          setRemoveFromSocial(false);
                          setOpenUnpublishConfirm(true);
                        }}
                        sx={listActionBtnStyle}
                      >
                        Unpublish
                      </Button>
                    )}
                    {post.status === "pending" ? (
                      <Button
                        size="small"
                        startIcon={<CalendarMonthIcon sx={{ fontSize: 15 }} />}
                        sx={listActionBtnStyle}
                        onClick={() => {
                          setSelectedPost(post);
                          setScheduleMode("reschedule");
                          if (post.schedule_time) {
                            const hasTz = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(post.schedule_time);
                            const dt = new Date(hasTz ? post.schedule_time : post.schedule_time + "Z");
                            setScheduleDate(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`);
                            setScheduleTime(`${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`);
                          }
                          setOpenSchedule(true);
                        }}
                      >
                        Schedule
                      </Button>
                    ) : isInProgress(post.status) ? (
                      <Stack
                        direction="row" alignItems="center" spacing={0.75}
                        sx={{
                          px: 1.5, py: 0.5, borderRadius: "8px",
                          background: "rgba(59,130,246,0.08)",
                          border: "1px solid rgba(59,130,246,0.2)",
                        }}
                      >
                        <CircularProgress size={12} thickness={6} sx={{ color: "#60A5FA" }} />
                        <Typography sx={{ fontSize: 12.5, color: "#60A5FA", fontWeight: 600 }}>
                          Processing…
                        </Typography>
                      </Stack>
                    ) : post.status === "draft" ? null : (
                      <Button
                        size="small"
                        startIcon={<SendOutlinedIcon sx={{ fontSize: 15 }} />}
                        onClick={() => goToPublishSchedule(post)}
                        sx={listPublishBtnStyle}
                      >
                        Publish
                      </Button>
                    )}
                  </Stack>


                  {/** <IconButton size="small" sx={{ color: "rgba(255,255,255,0.45)", flexShrink: 0 }}>
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconButton>*/}

                </Box>
              );
            })}
          </Box>
        )}

        {filteredPosts.length > 0 && (
          <>


            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                Page {page} of {totalPages || 1}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  size="small"
                  onClick={() => {
                    setPaginationLoadingKey("prev");
                    setPage((p) => p - 1);
                  }}
                  disabled={page === 1 || initiatedFetching}
                  sx={{
                    color: "rgba(255,255,255,0.55)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "8px", width: 30, height: 30,
                    "&.Mui-disabled": { opacity: 0.3 },
                  }}
                >
                  {paginationLoadingKey === "prev" && initiatedFetching
                    ? <CircularProgress size={12} sx={{ color: "inherit" }} />
                    : <span style={{ fontSize: 13 }}>‹</span>}
                </IconButton>
                {Array.from({ length: totalPages || 1 }, (_, i) => i + 1)
                  .slice(Math.max(0, page - 3), page + 2)
                  .map((p) => (
                    <IconButton
                      key={p}
                      size="small"
                      onClick={() => {
                        setPaginationLoadingKey(p);
                        setPage(p);
                      }}
                      disabled={initiatedFetching}
                      sx={{
                        width: 30, height: 30,
                        background: p === page ? "#4BA5EB" : "transparent",
                        color: p === page ? "#fff" : "rgba(255,255,255,0.55)",
                        borderRadius: "8px",
                        border: "1px solid rgba(255,255,255,0.08)",
                        fontSize: 12, fontWeight: 600,
                        "&:hover": { background: p === page ? "#4BA5EB" : "rgba(255,255,255,0.06)" },
                        "&.Mui-disabled": { opacity: p === page ? 0.85 : 0.3, color: p === page ? "#fff" : "rgba(255,255,255,0.55)" },
                      }}
                    >
                      {paginationLoadingKey === p && initiatedFetching
                        ? <CircularProgress size={12} sx={{ color: "inherit" }} />
                        : p}
                    </IconButton>
                  ))}
                <IconButton
                  size="small"
                  onClick={() => {
                    setPaginationLoadingKey("next");
                    setPage((p) => p + 1);
                  }}
                  disabled={page === totalPages || !totalPages || initiatedFetching}
                  sx={{
                    color: "rgba(255,255,255,0.55)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "8px", width: 30, height: 30,
                    "&.Mui-disabled": { opacity: 0.3 },
                  }}
                >
                  {paginationLoadingKey === "next" && initiatedFetching
                    ? <CircularProgress size={12} sx={{ color: "inherit" }} />
                    : <span style={{ fontSize: 13 }}>›</span>}
                </IconButton>
              </Stack>
            </Box>
          </>
        )}
      </Box>

      {/* MODALS */}

      {/* ══════════════════ WELCOME WALKTHROUGH POPUP ══════════════════ */}
      {/* <Dialog
        open={openWelcome && userDetails?.logged_in === false}
        onClose={closeWelcome}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            background: "#ffffff",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: "center", position: "relative" }}>
          <IconButton
            onClick={closeWelcome}
            sx={{ position: "absolute", top: 12, right: 12, color: "#6b7280" }}
          >
            <CloseIcon />
          </IconButton>

          <Typography sx={{ fontSize: 26, fontWeight: 800, color: "#0f172a", mb: 1.5, textAlign: "center" }}>
            Welcome to OceSha!
          </Typography>
          <Typography sx={{ fontSize: 15, color: "#6b7280", lineHeight: 1.5, maxWidth: "90%", mx: "auto", mb: 3, textAlign: "center" }}>
            We're excited to have you on board.<br />
            Watch this quick walkthrough to get familiar with the platform.
          </Typography>

          
          <Box
            sx={{
              position: "relative",
              borderRadius: "14px",
              overflow: "hidden",
              background: "#0b1220",
              aspectRatio: "16 / 10",
              mb: 2,
              cursor: welcomePlaying ? "default" : "pointer",
            }}
            onClick={() => { if (!welcomePlaying) setWelcomePlaying(true); }}
          >
            {!welcomePlaying ? (
              <>
                <Box sx={{
                  position: "absolute", inset: 0,
                  background:
                    "radial-gradient(circle at 70% 50%, #1e3a8a33, transparent 60%), linear-gradient(135deg,#0b1220 0%,#111c33 100%)",
                }} />
                <Box sx={{ position: "absolute", left: 28, top: "50%", transform: "translateY(-50%)", textAlign: "left", color: "#fff" }}>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 2 }}>◎ OceSha</Typography>
                  <Typography sx={{ fontSize: 26, fontWeight: 800 }}>Platform</Typography>
                  <Typography sx={{ fontSize: 26, fontWeight: 800, color: "#3b82f6" }}>Walkthrough</Typography>
                </Box>
                <Box sx={{
                  position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
                  width: 78, height: 54, borderRadius: "12px", background: "#ff0000",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
                }}>
                  <Box sx={{
                    width: 0, height: 0,
                    borderStyle: "solid", borderWidth: "13px 0 13px 22px",
                    borderColor: "transparent transparent transparent #fff", ml: "4px",
                  }} />
                </Box>
              </>
            ) : (
              <Box
                component="video"
                src="/images/Overview_new.mp4"
                controls
                autoPlay
                sx={{ width: "100%", height: "100%", display: "block", background: "#000" }}
              />
            )}
          </Box>

          <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 2 }}>
            ⏱ Video length: 6:19
          </Typography>

          <Button
            fullWidth
            variant="contained"
            onClick={() => setWelcomePlaying(true)}
            sx={{
              background: "linear-gradient(180deg,#3b82f6,#2563eb)",
              textTransform: "none", color: "#fff", fontSize: 16, fontWeight: 700,
              py: 1.5, borderRadius: "10px", boxShadow: "none",
              "&:hover": { background: "linear-gradient(180deg,#3b82f6,#2563eb)", opacity: 0.92, boxShadow: "none" },
            }}
          >
            Let's Get Started
          </Button>
          <Button
            fullWidth
            onClick={closeWelcome}
            sx={{ textTransform: "none", color: "#6b7280", fontSize: 14, mt: 1 }}
          >
            Maybe later
          </Button>
        </DialogContent>
      </Dialog> */}

      <Dialog open={openEditor} onClose={() => setOpenEditor(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", background: "#0F1117", border: "1px solid rgba(255,255,255,0.08)" } }}>
        <DialogContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography fontSize="16px" fontWeight={600}>Edit Post</Typography>
            <IconButton onClick={() => setOpenEditor(false)}><CloseIcon /></IconButton>
          </Stack>
          <TextField fullWidth multiline rows={6}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            placeholder="Edit your content..."
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { background: "rgba(255,255,255,0.03)", borderRadius: "10px" } }}
          />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => setOpenEditor(false)}
              sx={{ borderColor: "rgba(255,255,255,0.2)", color: "#aaa", textTransform: "none" }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleUpdatePost} disabled={updating}
              sx={{ background: "linear-gradient(90deg, #0168B5, #4BA5EB)", textTransform: "none" }}>
              {updating ? "Saving..." : "Save Changes"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={openSchedule} onClose={() => setOpenSchedule(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", background: "#0F1117", border: "1px solid rgba(255,255,255,0.08)" } }}>
        <DialogContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography fontSize="16px" fontWeight={600}>
              {scheduleMode === "schedule" ? "Schedule Post" : "Reschedule Post"}
            </Typography>
            <IconButton onClick={() => setOpenSchedule(false)}><CloseIcon /></IconButton>
          </Stack>
          <TextField type="date" fullWidth value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} />
          <TextField type="time" fullWidth value={scheduleTime}
            onChange={(e) => setScheduleTime(e.target.value)}
            sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => setOpenSchedule(false)}
              sx={{ borderColor: "rgba(255,255,255,0.2)", color: "#aaa", textTransform: "none" }}>
              Cancel
            </Button>
            <Button variant="contained" disabled={scheduling}
              onClick={async () => {
                if (!scheduleDate || !scheduleTime) { toast.error("Select date & time"); return; }
                try {
                  const iso = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
                  await schedulePost({ postId: selectedPost.id, email: userEmail, schedule_time: iso }).unwrap();
                  toast.success("Scheduled successfully");
                  setOpenSchedule(false);
                } catch { toast.error("Scheduling failed"); }
              }}
              sx={{ background: "linear-gradient(90deg, #0168B5, #4BA5EB)", textTransform: "none" }}>
              {scheduling ? "Scheduling..." : "Schedule"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={openCancelConfirm} onClose={() => setOpenCancelConfirm(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", background: "#0F1117", border: "1px solid rgba(255,255,255,0.08)" } }}>
        <DialogContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" mb={1.5}>
            <Typography fontSize="16px" fontWeight={600}>
              Cancel scheduled post?
            </Typography>
            <IconButton onClick={() => setOpenCancelConfirm(false)}><CloseIcon /></IconButton>
          </Stack>
          <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.6)", mb: 3 }}>
            {postToCancel?.schedule_time
              ? `This post won't be published on ${formatScheduleShort(postToCancel.schedule_time)}. You can reschedule it later.`
              : "This post won't be published as scheduled. You can reschedule it later."}
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => setOpenCancelConfirm(false)}
              sx={{ borderColor: "rgba(255,255,255,0.2)", color: "#aaa", textTransform: "none" }}>
              Keep it scheduled
            </Button>
            <Button variant="contained" disabled={cancelling} onClick={handleCancelScheduledPost}
              sx={{ background: "#DC2626", textTransform: "none", "&:hover": { background: "#B91C1C" } }}>
              {cancelling ? "Cancelling..." : "Cancel Post"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={openUnpublishConfirm} onClose={() => !unpublishing && setOpenUnpublishConfirm(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", background: "#0F1117", border: "1px solid rgba(255,255,255,0.08)" } }}>
        <DialogContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" mb={1.5}>
            <Typography fontSize="16px" fontWeight={600}>
              Unpublish this content?
            </Typography>
            <IconButton disabled={unpublishing} onClick={() => setOpenUnpublishConfirm(false)}><CloseIcon /></IconButton>
          </Stack>
          <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.6)", mb: 2 }}>
            It will be removed from your public profile page. You can publish it again any time.
          </Typography>

          {getLiveSocialPlatforms(postToUnpublish).length > 0 && (
            <Box sx={{ mb: 2.5, p: 1.5, borderRadius: "10px", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <input
                  type="checkbox"
                  id="unpublish-social-checkbox"
                  checked={removeFromSocial}
                  onChange={(e) => setRemoveFromSocial(e.target.checked)}
                  style={{ marginTop: 3, cursor: "pointer" }}
                />
                <label htmlFor="unpublish-social-checkbox" style={{ cursor: "pointer" }}>
                  <Typography sx={{ fontSize: 13.5, color: "#fff", fontWeight: 500 }}>
                    Also remove it from {getLiveSocialPlatforms(postToUnpublish).map(platformLabel).join(", ")}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)", mt: 0.25 }}>
                    This deletes the post from the connected account.
                    {getLiveSocialPlatforms(postToUnpublish).includes("youtube") &&
                      " YouTube videos can't be removed from here — delete them in YouTube Studio."}
                  </Typography>
                </label>
              </Stack>
            </Box>
          )}

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button variant="outlined" disabled={unpublishing} onClick={() => setOpenUnpublishConfirm(false)}
              sx={{ borderColor: "rgba(255,255,255,0.2)", color: "#aaa", textTransform: "none" }}>
              Keep published
            </Button>
            <Button variant="contained" disabled={unpublishing} onClick={handleUnpublishPost}
              sx={{ background: "#DC2626", textTransform: "none", "&:hover": { background: "#B91C1C" } }}>
              {unpublishing ? "Unpublishing..." : "Unpublish"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openClipInfoModal}
        onClose={() => setOpenClipInfoModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: "#0F1117",
            border: "1px solid rgba(255,255,255,0.08)",
          },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.2} mb={2}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px",
              background: "linear-gradient(135deg,#EA580C,#F97316)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <PlayCircleOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Typography fontSize="16px" fontWeight={600} color="#fff">
              Upload Content First
            </Typography>
          </Stack>

          <Typography fontSize="14px" color="rgba(255,255,255,0.7)" lineHeight={1.6} mb={3}>
            Video clip social post can be done after Video or Audio file uploaded.
          </Typography>

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => setOpenClipInfoModal(false)}
              sx={{
                borderColor: "rgba(255,255,255,0.2)",
                color: "#aaa",
                textTransform: "none",
                "&:hover": {
                  borderColor: "rgba(255,255,255,0.4)",
                  background: "rgba(255,255,255,0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setOpenClipInfoModal(false);
                localStorage.setItem("activeItem", "digital");
                navigate(0);
              }}
              sx={{
                background: "linear-gradient(90deg, #0168B5, #4BA5EB)",
                textTransform: "none",
                "&:hover": {
                  background: "linear-gradient(90deg, #0168B5, #4BA5EB)",
                  opacity: 0.9,
                },
              }}
            >
              OK
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <CustomizeBlogDialog
        open={openCustomize}
        onClose={() => setOpenCustomize(false)}
        data={selectedItem}
        onGenerate={handleCustomizedBlogGenerate}
      />

      <CustomizeCourseDialog
        open={openCustomizeCourse}
        onClose={() => setOpenCustomizeCourse(false)}
        data={selectedCourse}
        onGenerate={async (customizedData) => {
            console.log(customizedData);

            // Await the save-course call (handleSaveCourseAndPush is already
            // async and already sets its own loading/toast state). The dialog
            // stays open with its own spinner until this resolves, then closes.
            await handleSaveCourseAndPush(
                {
                    ...selectedCourse,
                    title: customizedData.title || selectedCourse?.title,
                    customization: customizedData,
                },
                "course-customized"
            );

            setOpenCustomizeCourse(false);
        }}
      />

      <Dialog
        open={upgradeModal.open}
        onClose={() =>
          setUpgradeModal((prev) => ({ ...prev, open: false }))
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            background: "#121212",
            color: "#fff",
            p: 1,
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <AutoAwesomeIcon
            sx={{
              fontSize: 50,
              color: "#F59E0B",
              mb: 2,
            }}
          />

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mb: 1,
            }}
          >
            {upgradeModal.title}
          </Typography>

          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 14,
              mb: 4,
            }}
          >
            {upgradeModal.message}
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() =>
                setUpgradeModal((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
              sx={{
                textTransform: "none",
                borderColor: "rgba(255,255,255,0.2)",
                color: "#fff",
              }}
            >
              {upgradeModal.title?.trim() === "Upload your data first"
                ? "Close"
                : "Cancel"}
            </Button>

            {upgradeModal.title?.trim() !== "Upload your data first" && upgradeModal.title?.trim() !== "Duplicte course title" && (
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  const value = {
                    _id: "",
                    product_name: "CodeGenie",
                    product_module: genieSelector.module || "",
                    userid: "",
                    productid: "",
                  };

                  getProduct(value);

                  localStorage.setItem("fromUpgrade", "true");

                  setUpgradeModal({
                    open: false,
                    title: "",
                    message: "",
                  });

                  navigate("/pricing", {
                    state: {
                      campIdFromUpgrade: selectedCampaign,
                      from_page: "pricing",
                      fromNonSubscribed: "true",
                    },
                  });
                }}
                sx={{
                  textTransform: "none",
                  background:
                    "linear-gradient(90deg,#0168B5 0%,#4BA5EB 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(90deg,#0168B5 0%,#4BA5EB 100%)",
                  },
                }}
              >
                Upgrade Plan
              </Button>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AuthorityEngine;
